import { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Autocomplete, Input, Modal, Spinner } from "src/components";
import { RootState } from "src/context/store";
import { useToast } from "src/hooks/useToast";
import { getCounterByEmployee, getCounterOrderProductsById, getCounterPoDetails, getInstorderCounterList, getStockist, postCounterPo, postCounterPoEdit, postPlaceOrder, validateCounterPo } from "src/services/order-api";
import PurpleLeftArrowIcon from "../../assets/purple-left-arrow.svg";
import Button from "../../components/Button";
import { ordPoActions } from "src/context/order-po-slice";
import { downloadInvoice } from "src/services/invoice-api";
import Download from "src/features/fields/assets/Download copy.svg";
import ToggleButton from "../../components/ToggleButton";
import DeleteIcon from "../../assets/delete.svg";
import editPen from "src/assets/edit-pen.svg";
import SubTable from "src/pages/counter-details/sub-table.component";
import { generateUniqueID } from "src/constants/functions";
import crossIcon from "src/assets/cross.svg";
import downArrow from "src/assets/down-arrow.svg";
import useDebounceLeading from "src/hooks/useDebounceLeading";
import { useEventStatus } from "src/hooks/useEventStatus";
import { useCorpEventStatus } from "src/hooks/useCorpEventStatus";
//M10-15149
import usePermission from "src/hooks/usePermission";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
//M10-15149
// PO-REJECTION-RESTRUCTURE ==================================================
import PoRejectModal from "./PoRejectModal";
import PoRejectionRevokeModal from "./PoRejectionRevokeModal";
import PoRejectionDetailsCard from "./PoRejectionDetailsCard";
// PO-REJECTION-RESTRUCTURE ==================================================
// COUNTER-PO-REVERT ==================================================
import PoRevertModal from "./PoRevertModal";
// COUNTER-PO-REVERT ==================================================

// Constants
// Controls whether PTR/PTS/MRP are shown per-strip (as stored) or divided down to a per-tablet value — stored per line item
const PRICE_UNIT_OPTIONS = [
    { label: "Strip" },
    { label: "Tablet" },
];

const INITIAL_FORM_STATE = {
    productName: null,
    productCode: null,
    priceIdentifier: null,
    schemeValue: "",
    quantity: "",
    poQuantity: '',
    poFreeQuantity: '',
    ptr: "",
    perStrip: "",
    pts: "",
    mrp: "",
    unitRate: "",
    discAmount: "",
    discPerc: "",
    gst: "",
    priceUnit: PRICE_UNIT_OPTIONS[0],
};

const INITIAL_ERRORS = {
    poRefNo: "",
    poDate: "",
    poExpiryDate: "",
    uploadSourceType: "",
    counterPoStatus: "",
    netAmount: "",
    counter: "",
};

const UPLOAD_SOURCE_TYPE_OPTIONS = [
    { label: "Proper PO" },
    { label: "Whatsapp Image" },
    { label: "Excel File" },
    { label: "Word Document" },
    { label: "Email Image" },
    { label: "Handwritten" },
];
const COUNTER_PO_STATUS_OPTIONS = [
    { label: "Draft" },
    { label: "On-Hold" },
    { label: "Rejected" },
    { label: "Not Approved" },
    { label: "Approved" },
];


// Hardcoded like priceIdentifierList in src/pages/invoice-details/invoice-constants.tsx — these options are fixed,
// so there's no need to fetch them per company/division from the Price Identifier API.
const PRICE_IDENTIFIER_LIST = [
    { id: 1, priceldentifier_desc: "Discount On PTR" },
    { id: 2, priceldentifier_desc: "Scheme" },
    { id: 3, priceldentifier_desc: "Fixed Price" },
];

const PER_STRIP_DIVIDED_FIELDS = ["ptr", "pts", "mrp", "quantity", "schemeQuantity"];
const PER_STRIP_MULTIPLIED_FIELDS = ["schemeValue"];

// PTR/PTS/MRP/Quantity are stored per-strip; when viewing per-tablet, divide down for display. Scheme Value goes the other way — multiply up.
// Tablet/Strip is a per-line-item choice (formData.priceUnit / lineItem.priceUnit), so the unit is always passed in rather than read off shared state.
// Module scope, not a component const: grandTotal's useMemo runs before the component body reaches this point.
const toDisplayValue = (field: string, rawValue: any, perStrip: any, unit: any) => {
    const strip = Number(perStrip || 0);
    if (unit?.label === "Tablet" && strip > 0 && PER_STRIP_DIVIDED_FIELDS.includes(field)) {
        const num = Number(rawValue || 0);
        return num ? Number((num / strip).toFixed(2)) : "";
    }
    if (unit?.label === "Tablet" && strip > 0 && PER_STRIP_MULTIPLIED_FIELDS.includes(field)) {
        const num = Number(rawValue || 0);
        return num ? Number((num * strip).toFixed(2)) : "";
    }
    return rawValue;
};

// scheme_value is a RATE under Discount On PTR (1) and Fixed Price (3), so it scales up per strip,
// but under Scheme (2) it is the FREE QUANTITY, which scales down like quantity.
const schemeValueField = (priceIdentifierId: any) =>
    Number(priceIdentifierId) === 2 ? "schemeQuantity" : "schemeValue";

// Quantity is held in whichever unit the user typed; everything downstream wants strips.
const getStripQuantity = (item: any) =>
    Number(toDisplayValue("quantity", item?.quantity, item?.perStrip, item?.priceUnit)) || 0;

const DEFAULT_COORDINATES = { latitude: 40.7128, longitude: -74.006 };

// PO Expiry Date falls back to 15 days after the PO Date when left blank (YYYY-MM-DD)
const getDefaultPoValidDate = (poDate: string) => {
    const d = new Date(poDate);
    d.setDate(d.getDate() + 15);
    return d.toISOString().split("T")[0];
};

// "YYYY-MM-DD" for an <input type="date"> from a saved ISO datetime (blank when missing)
const toDateInputValue = (value: any) => (value ? String(value).slice(0, 10) : "");

// Editable pricing fields shown once a product is picked in the kam-po-digitize flow
const PRICING_FIELDS = [
    { label: "PTR", field: "ptr" },
    { label: "PTS", field: "pts" },
    { label: "MRP", field: "mrp" },
    { label: "Per Strip", field: "perStrip" },
    // { label: "Unit Rate", field: "unitRate" },
    { label: "GST", field: "gst" },
];

const PoOrdersProductScreen = () => {
    const { locationCode } = useParams();
    const location = useLocation();
    const { OrderPoDetails, isStockist, existingCounterPoData } = location.state || {};
    const { selectedCounterData, productsData } = useSelector((state: RootState) => state.ordPoState);
    const { idTokenDetails, currentPsr } = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { toast } = useToast();
    // Without this permission the screen is view-only: no editing header fields, adding/editing/deleting
    // products, or approving the order.
    //M10-15149
    const { hasPermission: canEditProcessedPoPermission } = usePermission({
        module: fineGrainedPermissionsKeys.ORDER.PROCESSEDPODETAILS.module,
        action: fineGrainedPermissionsKeys.ORDER.PROCESSEDPODETAILS.action,
    });
    // M10-15732 previously restricted editing a processed PO to company_code 618 only.
    // COUNTER_PO_EDIT: that restriction is lifted — every company may now edit a processed
    // PO (subject to the ORDER.PROCESSEDPODETAILS permission as before). The real gate is
    // enforced server-side (editCounterPoProducerController / editCounterPo): once this PO
    // has a linked order, that order must still be 'Order Placed' or 'Order Escalated', or
    // the submit is rejected and its error message surfaced via the toast in handleApproval.
    const canEditProcessedPo = canEditProcessedPoPermission;
    //M10-15149
    // PO-REJECTION-RESTRUCTURE ==================================================
    // Reject is offered for every PO status except one already in "Reject" status;
    // once it IS Rejected, Reject is replaced by Revoke/Re-edit instead — never
    // show "Reject PO" on an already-rejected PO.
    // stockist_po_attachment_id can come from either the reopened counter PO
    // (existingCounterPoData) or the row the user clicked straight from the PO list
    // (OrderPoDetails, e.g. a still-Pending PO that has no saved counter PO yet).
    const [rejectPoOpen, setRejectPoOpen] = useState(false);
    const [rejectionRevokeOpen, setRejectionRevokeOpen] = useState(false);
    const [rejectionReEditOpen, setRejectionReEditOpen] = useState(false);
    const rejectPoStockistAttachmentId = existingCounterPoData?.stockist_po_attachment_id || OrderPoDetails?.stockist_po_attachment_id;
    // operation_status here is poid_status_hdr.poid_status_desc (e.g. "Rejected"), not the
    // lowercase order-domain ActionType enum ("reject") — substring match, not exact equality.
    // Counter PO events are published on the counter_po_<stockist_po_attachment_id> stream (see
    // counterPo.producer.controller.ts) — if that stream is blocked, show the banner like InvoiceApprovalScreen.
    const { withEventCheck, isBlocked: isCounterPoEventBlocked } = useEventStatus();
    const counterPoStreamId = rejectPoStockistAttachmentId ? `counter_po_${rejectPoStockistAttachmentId}` : undefined;
    const counterPoCompanyCode = Number(selectedCounterData?.company_code?.code) || Number(OrderPoDetails?.company_code) || undefined;
    useEffect(() => {
        if (!counterPoStreamId) return;
        withEventCheck(() => { }, counterPoStreamId, counterPoCompanyCode).catch(() => { });
    }, [counterPoStreamId, counterPoCompanyCode, withEventCheck]);

    // M10-16180: counter master events are published on the corp counter_<counter_id> stream (same stream
    // counter-api.ts checks). While it's blocked the counter can't be entered against — checked on load for
    // the banner, and again on Header Submit / Approve so a counter that became blocked meanwhile is caught.
    const { withCorpEventCheck } = useCorpEventStatus();
    const [isCounterEventBlocked, setIsCounterEventBlocked] = useState(false);
    const poCounterId = selectedCounterData?.counter_location?.counter_hdr_id?.counter_id || OrderPoDetails?.counter_location?.counter_hdr_id?.counter_id;
    const counterStreamId = poCounterId ? `counter_${poCounterId}` : undefined;
    // Resolves true when the counter's stream is clear (or there's no counter to check yet)
    const checkCounterEventStatus = async (showToast = true): Promise<boolean> => {
        if (!counterStreamId) return true;
        try {
            await withCorpEventCheck(() => { }, counterStreamId);
            setIsCounterEventBlocked(false);
            return true;
        } catch (error) {
            const isBlocked = error instanceof Error && /blocked/i.test(error.message);
            if (isBlocked) setIsCounterEventBlocked(true);
            if (showToast) {
                toast({
                    description: isBlocked
                        ? "This counter has blocked events. The PO cannot be entered until they are cleared."
                        : "Unable to verify the counter's event status. Please try again.",
                    variant: "destructive",
                });
            }
            return false;
        }
    };
    useEffect(() => {
        checkCounterEventStatus(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [counterStreamId]);
    const isAlreadyRejectedStatus = !!(existingCounterPoData?.operation_status ?? OrderPoDetails?.operation_status)?.toString()?.toLowerCase()?.includes("reject");
    // M10-15868: poid_status_id 2 = Approved — an Approved PO can no longer be rejected, and
    // Approve Order no longer applies (it's already approved), so both are removed entirely.
    const isApprovedStatus = (existingCounterPoData?.poid_status_id ?? OrderPoDetails?.poid_status_id) === 2;
    // Dedicated per-action permissions under the Orders module (order:counter_po/reject etc.) —
    // the Orders-->PO tab's counter_po flow, not the separate "Purchase Order" section's own
    // "po" module. Same keys PoCard.tsx's dropdown uses on the PO list.
    const { hasPermission: hasPermissionRejectPo } = usePermission({
        module: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECT.module,
        action: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECT.action,
    });
    const { hasPermission: hasPermissionRejectionRevoke } = usePermission({
        module: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECTIONREVOKE.module,
        action: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECTIONREVOKE.action,
    });
    const { hasPermission: hasPermissionRejectionReEdit } = usePermission({
        module: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECTIONREEDIT.module,
        action: fineGrainedPermissionsKeys.ORDER.COUNTERPOREJECTIONREEDIT.action,
    });
    const canRejectPo = Boolean(rejectPoStockistAttachmentId && hasPermissionRejectPo && !isAlreadyRejectedStatus && !isApprovedStatus); //M10-15868
    const canRevokePoRejection = Boolean(rejectPoStockistAttachmentId && hasPermissionRejectionRevoke && isAlreadyRejectedStatus);
    const canReEditPoRejection = Boolean(rejectPoStockistAttachmentId && hasPermissionRejectionReEdit && isAlreadyRejectedStatus);
    const rejectPoExistingRejectionCode = existingCounterPoData?.rejection_code ?? OrderPoDetails?.rejection_code;
    const rejectPoExistingRemarks = existingCounterPoData?.rejection_data_desc?.remarks ?? OrderPoDetails?.rejection_data_desc?.remarks;
    // PO-REJECTION-RESTRUCTURE ==================================================
    // COUNTER-PO-REVERT ==================================================
    // Sends an Approved/Processed PO back to Pending; the backend itself enforces the actual
    // "no active linked order" rule (throws if a linked order exists and isn't Cancelled), so this
    // gating just decides when the button is worth showing at all — anything already Pending or
    // Rejected has nothing to revert (Rejected uses its own Revoke/Re-edit flow instead).
    const [revertPoOpen, setRevertPoOpen] = useState(false);
    const { hasPermission: hasPermissionRevertPo } = usePermission({
        module: fineGrainedPermissionsKeys.ORDER.COUNTERPOREVERT.module,
        action: fineGrainedPermissionsKeys.ORDER.COUNTERPOREVERT.action,
    });
    const isAlreadyPendingStatus = (() => {
        const status = (existingCounterPoData?.operation_status ?? OrderPoDetails?.operation_status)?.toString()?.toLowerCase();
        return !status || status === "pending";
    })();
    const canRevertPo = Boolean(rejectPoStockistAttachmentId && hasPermissionRevertPo && !isAlreadyRejectedStatus && !isAlreadyPendingStatus);
    // COUNTER-PO-REVERT ==================================================
    // State
    const [isLoading, setIsLoading] = useState(false);
    // ADD-PRODUCT-REDESIGN: visual-only collapse toggle for the Add Product card, mirroring
    // InvoiceEntryScreen's "Invoice Details" card header (title + chevron, collapsible body)
    const [isAddProductExpanded, setIsAddProductExpanded] = useState(true);
    // M10-15868: Add Product sits above Product List — editing a row from the list (further down
    // the page) needs to expand + scroll back up to it, or the populated form goes unnoticed.
    const addProductSectionRef = useRef<HTMLDivElement>(null);
    const [priceIdentifier, setPriceIdentifier] = useState<any[]>([]);
    const [poRefNo, setPoRefNo] = useState("");
    const [poDate, setPoDate] = useState("");
    const [poExpiryDate, setPoExpiryDate] = useState("");
    const [selectedStockist, setSelectedStockist] = useState<any>([]);
    const [stockistList, setStockistList] = useState<any[]>([]);
    const [selectedCounter, setSelectedCounter] = useState<any>(null);
    const [counterList, setCounterList] = useState<any[]>([]);
    // Lets the KAM override an already-linked counter too — starts open automatically when none is linked yet
    const [isEditingCounter, setIsEditingCounter] = useState(() => {
        const kamPoDigitize = OrderPoDetails?.kam_type === 'kam-po-digitize';
        return kamPoDigitize && !OrderPoDetails?.counter_code && !OrderPoDetails?.counter_location?.counter_hdr_id?.counter_code;
    });
    const [isModelOpen, setIsModelOpen] = useState(false);
    const [errors, setErrors] = useState(INITIAL_ERRORS);
    const [isCustomerVerified, setIsCustomerVerified] = useState(false);
    const [isStatusVerified, setIsStatusVerified] = useState(false);
    const [isPoDatePresent, setIsPoDatePresent] = useState(false);
    const [isPoNumberPresent, setIsPoNumberPresent] = useState(false);
    // Net amount is required by default; toggling this ON marks it as not available so the user can skip it
    const [isNetAmountMissing, setIsNetAmountMissing] = useState(false);
    // M10-15818: toggling this ON drops GST from every line's total/net-amount calc and from the gst
    // sent per product line, for POs where the counter genuinely isn't charged GST
    const [isWithoutGst, setIsWithoutGst] = useState(false);
    const [netAmountInput, setNetAmountInput] = useState("");
    // PO-CASH-DISCOUNT: header-level cash discount, stored on stockist_po_attachment.cash_discount /
    // cash_discount_perc — mirrors InvoiceEntryScreen's cashDiscountAmount/cashDiscountPercent pair
    // (editing either one re-derives the other off calculatedNetAmount).
    const [cashDiscountInput, setCashDiscountInput] = useState("");
    const [cashDiscountPercentInput, setCashDiscountPercentInput] = useState("");
    const [uploadSourceType, setUploadSourceType] = useState<any>(null);
    const [counterPoStatus, setCounterPoStatus] = useState<any>(null);
    const [lineItems, setLineItems] = useState<any[]>([]);
    const [formData, setFormData] = useState<any>(INITIAL_FORM_STATE);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    // Fixed Price is the default Price Identifier for new items; populated once the price-identifier list loads
    const fixedPriceIdentifierRef = useRef<any>(null);
    // Focused automatically once a product is picked via Product Name/Code, so the user can go straight to typing the quantity
    const quantityInputRef = useRef<HTMLInputElement>(null);
    // Focused automatically after Add Item, so the user can go straight into the next product
    const productNameInputRef = useRef<HTMLInputElement>(null);
    // Guards the existingCounterPoData prefill effect below so it only seeds the form once, not on every re-render
    const hasPrefilledExistingPo = useRef(false);
    // Lets the user type a product that isn't in the master list — Product Name/Code become free text and pricing fields become editable (mirrors the "Unknown Order" flow on the Invoice Entry screen)
    const [isUnknownProduct, setIsUnknownProduct] = useState(false);
    // M10-16315: bumped to remount Product Name/Code when an inactive pick is swapped or rejected,
    // otherwise the Autocomplete keeps showing the inactive item it selected internally
    const [productPickerKey, setProductPickerKey] = useState(0);
    const [showDiscountConfirm, setShowDiscountConfirm] = useState(false);
    const pendingActionRef = useRef<"add" | "update" | null>(null);
    // M10-15732: Headless UI's Dialog auto-focuses the first focusable element (Cancel), so pressing
    // Enter right after the modal opens would cancel instead of confirm. Focus Confirm & Add instead.
    const confirmHighDiscountButtonRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (!showDiscountConfirm) return;
        const timer = setTimeout(() => confirmHighDiscountButtonRef.current?.focus(), 0);
        return () => clearTimeout(timer);
    }, [showDiscountConfirm]);
    // Gates the whole Add Product section for the digitize flow — products can only be added once
    // /api/v1/oms/counter_po/validate confirms this PO. "checking"/"invalid" both block Add Item.
    const [counterPoValidation, setCounterPoValidation] = useState<{ status: "idle" | "checking" | "valid" | "invalid"; message?: string }>({ status: "idle" });
    // This screen serves three overlapping flows, gated by these three flags:
    //  - isStockist: the plain stockist-PO flow (shows PO Quantity/Free Quantity, opens the stockist-select modal)
    //  - isKam: legacy flag controlling whether KAM pricing (PTR/Scheme Value/Grand Total) is visible to the current user
    //  - isKamPoDigitize: the newer "digitize a paper PO" flow — posts to a different API (COUNTER_PO_ADD)
    //    with its own header fields (PO Number/Date "present" toggles, Upload Source Type, Counter PO Status)
    //    and its own editable per-product pricing panel
    const isKAMSession = window.sessionStorage.getItem("kam_price_visibility");
    const isKam = (OrderPoDetails?.kam_price_visibility === "Y" || OrderPoDetails?.kam_price_visibility == null) && isKAMSession === 'Y';
    const isKamPoDigitize = OrderPoDetails?.kam_type === 'kam-po-digitize';
    // True when this PO has no counter linked yet — the KAM must pick one from instorder-counter-list rather than just verifying a pre-matched name
    const isCounterUnlinked = isKamPoDigitize && !OrderPoDetails?.counter_code && !OrderPoDetails?.counter_location?.counter_hdr_id?.counter_code;
    // Blocks the Add Product section until Header Submit's /api/v1/oms/counter_po/validate confirms this PO
    // (every flow — Approve already requires it), while the counter has blocked events (M10-16180),
    // or entirely when the user lacks permission to edit this PO (view-only access).
    // Header Submit (COUNTER_PO_EDIT / counter_po/validate) is keyed on stockist_po_attachment_id — a PO without
    // one can't be header-submitted, so the Header Submit requirement only applies when it exists.
    const requiresHeaderSubmit = Boolean(OrderPoDetails?.stockist_po_attachment_id);
    const isHeaderSubmitPending = requiresHeaderSubmit && counterPoValidation.status !== "valid";
    const isAddProductBlocked = isHeaderSubmitPending || isCounterEventBlocked || !canEditProcessedPo; //M10-15149
    //M10-14422 — Hide PO Quantity / PO Free Quantity inputs and columns when company kam_type is "kam-rc"
    const kamType =
        selectedCounterData?.company_code?.kam_type ??
        OrderPoDetails?.company_code?.kam_type ??
        "";
    const showStockistPo = isStockist && kamType !== "kam-rc";

    // Memoized header data
    const headerData = useMemo(() => [
        { label: "Company Name", value: OrderPoDetails?.company_name || OrderPoDetails?.company_code?.name },
        { label: "Stockist Code", value: OrderPoDetails?.stockist_medvol_code || selectedStockist?.owner_name },
        { label: "Stockist Name", value: OrderPoDetails?.stockist_name || selectedStockist?.medvol_stockist_code },
        // selectedCounter must win once the user picks a new counter from the editor — otherwise
        // the header keeps showing the PO's original (stale) counter name/code even after the
        // change, same reasoning as Location Name/Code below.
        { label: "Counter Name", value: selectedCounter?.counter_name || OrderPoDetails?.counter_name || OrderPoDetails?.counter_location?.counter_hdr_id?.counter_name },
        { label: "Counter Code", value: selectedCounter?.counter_code || OrderPoDetails?.counter_code || OrderPoDetails?.counter_location?.counter_hdr_id?.counter_code },
        // OrderPoDetails.location_name/location_code are just "0"/blank placeholders when no counter is linked yet —
        // once the KAM picks one from the dropdown, the picked counter's data must win over those placeholders
        { label: "Location Name", value: isCounterUnlinked ? (selectedCounterData?.counter_location?.location_name || selectedCounter?.location_name) : (OrderPoDetails?.location_name || OrderPoDetails?.counter_location?.location_name) },
        { label: "Location Code", value: isCounterUnlinked ? (selectedCounter?.location_code || selectedCounterData?.counter_location?.location_code) : (OrderPoDetails?.location_code || OrderPoDetails?.counter_location?.location_code) },
        { label: "PO ID Number", value: OrderPoDetails?.poid_number },
        { label: "City", value: selectedCounterData?.counter_location?.city_code?.city_name },
        { label: "State", value: selectedCounterData?.counter_location?.city_code?.district_code?.state_code?.state_name || selectedCounter?.state },
        { label: "Counter GST", value: selectedCounterData?.counter_location?.counter_hdr_id?.gst_number || selectedCounter?.gst_number },
        { label: "Company Stockist Code", value: OrderPoDetails?.company_stockist_code || selectedStockist?.company_stockist_code },
        { label: "Address", value: selectedCounterData?.counter_location?.address || selectedCounter?.address },
        { label: "Hosiptal Name", value: OrderPoDetails?.novu_metadata?.hospitalName },
        // Fields mirrored from the Invoice Details header (getInvoiceDetailsInfo) — only the ones
        // with a real PO equivalent; invoice-only fields (GST Applicability, IRN, MCI, E-Inv,
        // Textracted, Auto Entered) don't apply here and are intentionally left out.
        { label: "District Name", value: selectedCounterData?.counter_location?.city_code?.district_code?.district_name },
        { label: "FUID", value: OrderPoDetails?.fuid_no },
        { label: "Link Type", value: OrderPoDetails?.novu_metadata?.counterType },
        { label: "Order Code", value: OrderPoDetails?.order_codes },
        { label: "Entered By", value: OrderPoDetails?.entered_by },
        {
            label: "Entered Date",
            value: OrderPoDetails?.entered_date
                ? `${new Date(OrderPoDetails.entered_date).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                })}, ${new Date(OrderPoDetails.entered_date).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                })}`
                : "",
        },
    ], [OrderPoDetails, selectedCounterData, selectedStockist, selectedCounter]);

    // Memoized filtered products
    const filteredProducts = useMemo(() => {
        return productsData?.filter(
            (p: any) => !lineItems.some(
                item => item.productCode?.companyProducts?.item_code === p.companyProducts?.item_code
            )
        ) || [];
    }, [productsData, lineItems]);

    // Same counter_company_location_id fallback chain used to build the counter-po payload — needed here too since
    // a reopened Processed PO can have selectedCounter/selectedCounterData still empty.
    const activeCounterLocationId = selectedCounter?.countercompanylnk_id
        || selectedCounterData?.counter_company_location_id
        || OrderPoDetails?.counter_company_location_id
        || window.sessionStorage.getItem("counterCompanyLocationId")
        || locationCode;

    // Hits /oms/counter/products/location as the user types into Product Name/Product Code, so the dropdown reflects
    // a live server-side search instead of a product list preloaded at mount. Already-added line items are
    // excluded, mirroring filteredProducts above. Results are merged into the shared productsData store since a few
    // downstream payload builders (item_division_id, cluster_code, etc.) and the stockist-list effect key off
    // productsData[0] for this counter's division metadata, and nothing pre-populates it anymore.
    const searchProductsByField = async (query: string, field: "item_code" | "product_name") => {
        const res = await getCounterOrderProductsById(
            activeCounterLocationId,
            field === "item_code" ? query : "",
            field === "product_name" ? query : "",
            "4",
            undefined,
            "po"
        );
        const results = res?.data?.data || [];

        results.forEach((product: any) => {
            if (product?.counter_ff_div_id?.cmp_countertag?.is_division_order_required === true) {
                window.sessionStorage.setItem("iSalesRequired", product?.counter_ff_div_id?.cmp_countertag?.is_division_order_required);
            }
        });
        const merged = [...(productsData || [])];
        // M10-16315: replacements are auto-selected in place of inactive items, so they need to be in the store too
        const resultsWithReplacements = results.flatMap((item: any) => (item?.replaced_product ? [item, item.replaced_product] : [item]));
        resultsWithReplacements.forEach((item: any) => {
            if (!merged.some((p: any) => p.companyProducts?.item_code === item.companyProducts?.item_code)) merged.push(item);
        });
        dispatch(ordPoActions.setProductsData(merged));

        return results.filter(
            (p: any) => !lineItems.some(item => item.productCode?.companyProducts?.item_code === p.companyProducts?.item_code)
        );
    };
    const searchProductsByName = (query: string) => searchProductsByField(query, "product_name");
    const searchProductsByCode = (query: string) => searchProductsByField(query, "item_code");

    // Memoized grand total
    const grandTotal = useMemo(
        () =>
            lineItems
                .reduce((sum, item) => {
                    const quantity = getStripQuantity(item);
                    const ptr = Number(item?.productCode?.companyProducts?.ptr || 0);
                    const schemeValue = Number(item?.schemeValue || 0);

                    let price = ptr;

                    if (item?.priceIdentifier?.id === 3) {
                        // Scheme price directly
                        price = schemeValue;
                    } else if (item?.priceIdentifier?.id === 1) {
                        // PTR with percentage discount
                        price = ptr - (ptr * schemeValue) / 100;
                    }

                    return sum + price * quantity;
                }, 0)
                .toFixed(2),
        [lineItems]
    );

    // Per-line net amount: price (after scheme/discount) * quantity, plus GST — shared by the Total column and the calculated net amount hint below
    // Uses discPerc (a unit-agnostic ratio) rather than the raw discAmount, since discAmount may have been typed
    // while viewing per-Tablet PTR but ptr here is always the raw per-Strip value — a % works for both.
    const getLineTotal = (item: any) => {
        const quantity = getStripQuantity(item);
        const ptr = Number(item?.ptr || item?.productCode?.companyProducts?.ptr || 0);
        const discPerc = Number(item?.discPerc || 0);
        const gst = isWithoutGst ? 0 : Number(item?.gst || item?.productCode?.companyProducts?.gst_pc || 0); //M10-15818

        let price = ptr;

        if (item?.priceIdentifier?.id === 3) {
            price = Number(item?.schemeValue || 0);
        } else {
            price = ptr - (ptr * discPerc) / 100;
        }

        const lineAmount = (price * quantity)-item.discAmount;
        return lineAmount + (lineAmount * gst) / 100;
    };

    // Calculated only as a hint shown in the Net Amount placeholder — the actual value is typed in by the user (see netAmountInput)
    const calculatedNetAmount = useMemo(
        () =>
            lineItems
                .reduce((sum, item) => sum + getLineTotal(item), 0)
                .toFixed(2),
        [lineItems, isWithoutGst] //M10-15818
    );

    // Handlers
    const handleChange = (field: string, value: any) => {
        // M10-16315: inactive items are listed (in red) at PO digitization — a replaced one is swapped for
        // its replacement, one without a replacement can't be entered
        if ((field === "productName" || field === "productCode") && value?.is_inactive) {
            const inactiveName = value?.companyProducts?.name;
            const replacement = value?.replaced_product;
            setProductPickerKey(prev => prev + 1);
            if (!replacement) {
                toast({ description: `${inactiveName} is inactive and has no replacement product — it cannot be added.`, variant: "destructive" });
                return;
            }
            const replacementCode = replacement.companyProducts?.item_code;
            const replacementName = replacement.companyProducts?.name;
            if (lineItems.some((item, i) => i !== editingIndex && item.productCode?.companyProducts?.item_code === replacementCode)) {
                toast({ description: `${inactiveName} is inactive and its replacement ${replacementName} is already added.`, variant: "destructive" });
                return;
            }
            toast({ description: `${inactiveName} is inactive — replaced with ${replacementName} (${replacementCode}).` });
            handleChange(field, replacement);
            return;
        }

        let updatedForm = { ...formData, [field]: value };

        // Sync productName and productCode automatically
        if (field === "productName") {
            updatedForm.productCode = value;
        } else if (field === "productCode") {
            updatedForm.productName = value;
        }

        // Prefill the editable pricing fields from the selected product; user can still edit them afterward
        if (field === "productName" || field === "productCode") {
            const companyProducts = value?.companyProducts;
            const ptr = Number(companyProducts?.ptr || 0);
            const perStrip = Number(companyProducts?.per_strip || 0) || "";
            updatedForm.ptr = ptr || "";
            updatedForm.perStrip = perStrip;
            updatedForm.pts = companyProducts?.pts ?? "";
            updatedForm.mrp = companyProducts?.mrp ?? "";
            updatedForm.unitRate = perStrip ? Number((ptr / perStrip).toFixed(2)) : ptr || "";
            updatedForm.discAmount = updatedForm.discAmount || "";
            updatedForm.discPerc = updatedForm.discPerc || "";
            updatedForm.gst = companyProducts?.gst_pc ?? "";
        }

        // Discount Amount and Discount Percentage are kept in sync against whichever PTR is currently on screen
        // (Tablet or Strip); Discount % is derived from that displayed PTR so it stays correct regardless of the
        // unit toggle, and the rest of the calculation (Total column, disc_perc/net_rate payload) works off this
        // unit-agnostic discPerc
        if (field === "discAmount") {
            const displayedPtr = Number(toDisplayValue("ptr", updatedForm.ptr, updatedForm.perStrip, updatedForm.priceUnit)) || 0;
            const amount = Number(value || 0);
            updatedForm.discPerc = displayedPtr > 0 ? Number(((amount / displayedPtr) * 100).toFixed(2)) : 0;
        } else if (field === "discPerc") {
            const displayedPtr = Number(toDisplayValue("ptr", updatedForm.ptr, updatedForm.perStrip, updatedForm.priceUnit)) || 0;
            const percent = Number(value || 0);
            updatedForm.discAmount = displayedPtr > 0 ? Number(((percent / 100) * displayedPtr).toFixed(2)) : 0;
        }

        setFormData(updatedForm);

        // Jump to Quantity once a product is picked; deferred since the Combobox reclaims focus on selection
        if ((field === "productName" || field === "productCode") && value) {
            setTimeout(() => quantityInputRef.current?.focus(), 0);
        }
    };

    const clearForm = () => {
        setFormData({ ...INITIAL_FORM_STATE, priceIdentifier: fixedPriceIdentifierRef.current });
    };

    // Product Name and Product Code share one synthesized "companyProducts" object in unknown-product mode, same shape a real product would have, so the rest of the screen (table columns, payload builders) doesn't need to special-case it
    const handleUnknownProductChange = (part: "name" | "code", value: string) => {
        setFormData((prev: any) => {
            const prevCompanyProducts = prev.productName?.companyProducts || prev.productCode?.companyProducts || {};
            const companyProducts = {
                ...prevCompanyProducts,
                ...(part === "name" ? { name: value } : { item_code: value }),
                isUnknown: true,
            };
            const productObj = { companyProducts };
            return { ...prev, productName: productObj, productCode: productObj };
        });
    };

    const handleUnknownProductToggle = (value?: boolean) => {
        setIsUnknownProduct(prev => value ?? !prev);
        clearForm();
    };

    // Discount % of PTR that Scheme Value undercuts, based on the currently displayed PTR (respects the Tablet/Strip toggle)
    const getDiscountPercent = (data: any) => {
        const displayedPtr = Number(toDisplayValue("ptr", data.ptr, data.perStrip, data.priceUnit)) || 0;
        const schemeValueNum = Number(data.schemeValue) || 0;
        console.log(displayedPtr,"displayedPtr")
        if (!displayedPtr) return 0;
        console.log(displayedPtr,schemeValueNum,"popo")
        return ((displayedPtr - schemeValueNum) / displayedPtr) * 100;
    };

    // In unknown-product mode the name/code are free text, so require them to actually be filled rather than just non-null
    const isProductInfoMissing = (data: any) =>
        isUnknownProduct
            ? !data.productName?.companyProducts?.name?.trim() || !data.productCode?.companyProducts?.item_code?.trim()
            : !data.productName || !data.productCode;

    // Deferred since Add Item is a plain button click, but keeps the same pattern used for the Quantity auto-focus above
    const focusProductNameField = () => {
        setTimeout(() => productNameInputRef.current?.focus(), 0);
    };

    const handleAddItem = () => {
        if (isAddProductBlocked) {
            toast({ description: isCounterEventBlocked ? "This counter has blocked events — products cannot be added." : counterPoValidation.message || "Please submit the header (Header Submit) before adding products.", variant: "destructive" });
            return;
        }
        if (isProductInfoMissing(formData) || !formData.priceIdentifier || !formData.quantity) {
            toast({ description: "Please fill all required fields", variant: "destructive" });
            return;
        }
        if (getDiscountPercent(formData) > 40) {
            pendingActionRef.current = "add";
            setShowDiscountConfirm(true);
            return;
        }
        setLineItems(prev => [...prev, formData]);
        clearForm();
        focusProductNameField();
    };

    const handleEditItem = (index: number, data: any) => {
        setFormData({ ...data });
        setEditingIndex(index);
        setIsUnknownProduct(!!data.productName?.companyProducts?.isUnknown);
        // M10-15868: Add Product is above Product List on the page — expand it (in case it was
        // collapsed) and scroll it into view so the now-populated form is actually visible.
        setIsAddProductExpanded(true);
        requestAnimationFrame(() => {
            addProductSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
    };

    const handleUpdateItem = () => {
        if (isAddProductBlocked) {
            toast({ description: isCounterEventBlocked ? "This counter has blocked events — products cannot be added." : counterPoValidation.message || "Please submit the header (Header Submit) before adding products.", variant: "destructive" });
            return;
        }
        if (isProductInfoMissing(formData) || !formData.priceIdentifier || !formData.quantity) {
            toast({ description: "Please fill all required fields", variant: "destructive" });
            return;
        }
        if (getDiscountPercent(formData) > 40) {
            pendingActionRef.current = "update";
            setShowDiscountConfirm(true);
            return;
        }

        setLineItems(prev => {
            const updated = [...prev];
            if (editingIndex !== null) updated[editingIndex] = formData;
            return updated;
        });

        clearForm();
        setEditingIndex(null);
    };

    const handleDeleteItem = (index: number) => {
        setLineItems(prev => prev.filter((_, i) => i !== index));
    };

    // User must confirm a >40% discount before the item is actually added/updated
    const handleConfirmHighDiscount = () => {
        const wasAdd = pendingActionRef.current === "add";
        if (pendingActionRef.current === "update" && editingIndex !== null) {
            setLineItems(prev => {
                const updated = [...prev];
                updated[editingIndex] = formData;
                return updated;
            });
            setEditingIndex(null);
        } else {
            setLineItems(prev => [...prev, formData]);
        }

        clearForm();
        setShowDiscountConfirm(false);
        pendingActionRef.current = null;
        if (wasAdd) focusProductNameField();
    };

    const handleCancelHighDiscount = () => {
        setShowDiscountConfirm(false);
        pendingActionRef.current = null;
    };

    // Shared "Add Item" / "Update Item" button — rendered in a different spot depending on the flow (see JSX below)
    const addOrUpdateItemButton = (
        <button
            onClick={editingIndex === null ? handleAddItem : handleUpdateItem}
            disabled={isAddProductBlocked}
            className="h-9 px-5 bg-[#8051FF] hover:bg-[#6f3fe0] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-normal rounded-md shadow-sm transition duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-1"
        >
            {editingIndex === null ? "Add Item" : "Update Item"}
        </button>
    );

    const handleCustomerToggle = () => setIsCustomerVerified(!isCustomerVerified);
    const handleStatusToggle = () => setIsStatusVerified(!isStatusVerified);

    const handlePoDatePresentToggle = () => {
        setIsPoDatePresent(prev => !prev);
        setErrors(prev => ({ ...prev, poDate: "", poExpiryDate: "" }));
    };
    const handlePoNumberPresentToggle = () => {
        setIsPoNumberPresent(prev => !prev);
        setErrors(prev => ({ ...prev, poRefNo: "" }));
    };
    const handleNetAmountMissingToggle = () => {
        setIsNetAmountMissing(prev => !prev);
        setErrors(prev => ({ ...prev, netAmount: "" }));
    };
    const handleWithoutGstToggle = () => setIsWithoutGst(prev => !prev); //M10-15818

    // M10-15868: base amount for the %<->amount derivation is always the line-items total, never
    // the typed Net Amount — mirrors InvoiceEntryScreen's calculateSubtotal(), which likewise never
    // factors in the typed net amount. Editing Net Amount must not affect Cash Discount % / Amount
    // or the "Calculated Net Amount" figure.
    const cashDiscountBaseAmount = () => Number(calculatedNetAmount) || 0;
    // M10-15868: the "Calculated Net Amount" shown on screen — line-items total less the cash
    // discount, independent of whatever is typed in Net Amount.
    const calculatedNetAmountDisplay = () => Number((cashDiscountBaseAmount() - (Number(cashDiscountInput) || 0)).toFixed(2));
    // M10-15868: the net_amount actually submitted is the user-typed/verified Net Amount itself
    // (mirroring InvoiceEntryScreen's `net_amount: netInvoiceAmount`), not the calculated figure —
    // validated to be within ₹10 of calculatedNetAmountDisplay() before submission is allowed.
    const calculatedNetAmountForSubmit = () =>
        isNetAmountMissing ? 0 : Number(netAmountInput) || 0;
    const handleCashDiscountPercentChange = (value: string) => {
        setCashDiscountPercentInput(value);
        const percent = parseFloat(value) || 0;
        const base = cashDiscountBaseAmount();
        setCashDiscountInput(base ? ((base * percent) / 100).toFixed(2) : "");
    };
    const handleCashDiscountAmountChange = (value: string) => {
        setCashDiscountInput(value);
        const amount = parseFloat(value) || 0;
        const base = cashDiscountBaseAmount();
        setCashDiscountPercentInput(base ? ((amount / base) * 100).toFixed(2) : "");
    };

    const handleDownloadPO = () => {
        if (!OrderPoDetails?.po_file_path) {
            toast({ description: "PO not found", variant: "destructive" });
            return;
        }
        downloadInvoice(OrderPoDetails?.po_file_path, OrderPoDetails?.company_code).then((res: any) => {
            if (res?.data?.success && res?.data?.data) {
                window.open(res?.data?.data, "_blank");
            } else {
                console.error("Failed to download the invoice.");
            }
        });
    };

    const getCurrentLocation = (): Promise<{ latitude: number; longitude: number }> => {
        return new Promise((resolve, reject) => {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        resolve({
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        });
                    },
                    (error) => {
                        reject(error);
                    }
                );
            } else {
                reject(new Error("Geolocation is not supported by this browser."));
            }
        });
    };

    // Maps one Add Product line item into the shape the counter-po API expects for kam-po-digitize orders
    const buildCounterPoOrderProduct = (item: any, index: number) => {
        const companyProducts = item?.productCode?.companyProducts;
        // Prefer the user-edited pricing values from the Add Product form; fall back to the product snapshot if left blank
        const ptr = Number(item?.ptr || companyProducts?.ptr || 0);
        // Kept as a number (not `undefined`) — axios/JSON.stringify drops undefined keys entirely, which was
        // silently omitting per_strip from the request body whenever a product had no per-strip value.
        const perStrip = Number(item?.perStrip || 0);
        const discPerc = Number(item?.discPerc || 0);
        const unitRate = Number(item?.unitRate || (perStrip ? ptr / perStrip : ptr));
        // Numeric fields default to 0 (not undefined) — axios/JSON.stringify drops undefined keys entirely,
        // which would silently omit them from the request body instead of sending a real 0.
        const pts = Number(item?.pts || companyProducts?.pts || 0);
        const mrp = Number(item?.mrp || companyProducts?.mrp || 0);
        // Quantity and Scheme Value are sent using the same Tablet/Strip conversion shown in the Product List table; PTR/PTS/MRP are sent as the raw stored values shown there
        const quantitySend = getStripQuantity(item);
        const schemeValueSend =
            Number(
                toDisplayValue(
                    schemeValueField(item?.priceIdentifier?.id),
                    item?.schemeValue,
                    perStrip,
                    item?.priceUnit,
                ),
            ) || 0;
        // discPerc is unit-agnostic (derived from whichever PTR was on screen when Discount Amount was typed), so it
        // stays correct here regardless of Tablet/Strip — a raw discAmount subtraction against this per-Strip ptr would not.
        const netRate = item?.priceIdentifier?.id === 3
            ? schemeValueSend
            : ptr - (ptr * discPerc) / 100;

        return {
            ucode: generateUniqueID(),
            item_code: companyProducts?.item_code ?? "",
            product_name: companyProducts?.name ?? "",
            // This line item's own product — not productsData[0], which is just whichever product was fetched
            // first and would otherwise stamp every line with the same division regardless of what it actually is.
            item_division_id: companyProducts?.item_division?.[0]?.item_divisiondtl_id ?? 0,
            company_division_code: item?.productCode?.counter_ff_div_id?.company_division_code?.company_divisioncode ?? 0,
            price_identifier: item?.priceIdentifier?.id ?? null,
            sequence_number: index + 1,
            scheme_value: schemeValueSend,
            stockist_po_quantity: quantitySend,
            stockist_po_freequantity: 0,
            per_strip: perStrip,
            ptr: ptr,
            pts: pts,
            mrp: mrp,
            unit_rate: Number(unitRate.toFixed(2)),
            disc_perc: discPerc,
            net_rate: Number(netRate.toFixed(2)),
            gst: isWithoutGst ? 0 : Number(item?.gst || companyProducts?.gst_pc || 0), //M10-15818
        };
    };

    // Builds the request body for the kam-po-digitize flow — POST /event/counter_po/, event_type COUNTER_PO_ADD
    const buildCounterPoPayload = (effectivePoDate: string, effectivePoRefNo: string, istISOString: string, latitude: number, longitude: number) => ({
        data: {
            company_code: Number(selectedCounterData?.company_code?.code) || Number(OrderPoDetails?.company_code),
            // Falls back to OrderPoDetails directly — for a reopened Processed PO whose counter link is already
            // resolved (counter_code present) but couldn't be re-fetched into selectedCounterData (e.g. a stale/
            // missing counter_company_location_id), selectedCounter/selectedCounterData stay empty and these
            // fields would otherwise go out as undefined, failing backend validation.
            counter_code: selectedCounter?.counter_code || selectedCounterData?.counter_location?.counter_hdr_id?.counter_code || OrderPoDetails?.counter_code || OrderPoDetails?.counter_location?.counter_hdr_id?.counter_code,
            counter_company_location_id: selectedCounter?.countercompanylnk_id || selectedCounterData?.counter_company_location_id || OrderPoDetails?.counter_company_location_id,
            location_code: selectedCounter?.location_code || selectedCounterData?.counter_location?.location_code || OrderPoDetails?.location_code || OrderPoDetails?.counter_location?.location_code,
            location_name: selectedCounter?.location_name || OrderPoDetails?.location_name || OrderPoDetails?.counter_location?.location_name,
            counter_name: selectedCounter?.counter_name || OrderPoDetails?.counter_name || OrderPoDetails?.counter_location?.counter_hdr_id?.counter_name,
            company_stockist_code: OrderPoDetails?.company_stockist_code || selectedStockist?.company_stockist_code,
            stockist_po_attachment_id: OrderPoDetails?.stockist_po_attachment_id,
            kam_type: OrderPoDetails?.kam_type,
            module_code: 4,
            poid_date: effectivePoDate,
            po_valid_date: poExpiryDate || getDefaultPoValidDate(effectivePoDate),
            poid_ref_number: effectivePoRefNo,
            reference_number: effectivePoRefNo,
            stockist_po_number: effectivePoRefNo,
            upload_source_type: uploadSourceType?.label,
            counter_po_status: counterPoStatus?.label,
            is_po_date_missing: !isPoDatePresent,
            is_po_number_missing: !isPoNumberPresent,
            is_po_netamount_missing: isNetAmountMissing,
            net_amount: calculatedNetAmountForSubmit(),
            cash_discount: Number(cashDiscountInput) || 0,
            cash_discount_perc: Number(cashDiscountPercentInput) || 0,
            // Audit/actor fields, kept consistent with the legacy ORDER_ADD payload below
            created_by: idTokenDetails?.sub || "",
            created_date: istISOString,
            source: "Web",
            latitude,
            longitude,
            actor_name: idTokenDetails?.name || "",
            actor_code: currentPsr?.emp_position_hdr?.position_code || "",
            actor_type: idTokenDetails?.["custom:role"],
            user_ucode: idTokenDetails?.["custom:ucode"] || "",
            ucode: generateUniqueID(),
            skip_notifications: true,
            orderProducts: lineItems.map(buildCounterPoOrderProduct),
        },
        event_type: "COUNTER_PO_ADD",
    });

    // Maps one line item for the COUNTER_PO_EDIT payload — same full snapshot shape as buildCounterPoOrderProduct
    // (COUNTER_PO_ADD), tagged with stockist_po_attachment_products_id: the real id for an item already saved on
    // this PO, or 0 for one the KAM added fresh this session (so the backend knows to insert it).
    const buildCounterPoEditOrderProduct = (item: any, index: number) => ({
        ...buildCounterPoOrderProduct(item, index),
        stockist_po_attachment_products_id: item?.stockistPoAttachmentProductId || 0,
    });

    // Builds the request body for resubmitting an already-saved counter PO (reopened Processed row) —
    // POST /event/counter_po/edit, event_type COUNTER_PO_EDIT.
    // Mirrors every header field buildCounterPoPayload (ADD) sends, plus the edit-specific edit_type/primary_key
    // and po_order_source (passed through as-is from the fetched PO, whatever is shown on screen for it).
    const buildCounterPoEditPayload = (effectivePoDate: string, effectivePoRefNo: string, istISOString: string, latitude: number, longitude: number) => ({
        data: {
            company_code: Number(selectedCounterData?.company_code?.code) || Number(OrderPoDetails?.company_code),
            counter_code: selectedCounter?.counter_code || selectedCounterData?.counter_location?.counter_hdr_id?.counter_code || OrderPoDetails?.counter_code || OrderPoDetails?.counter_location?.counter_hdr_id?.counter_code,
            counter_company_location_id: selectedCounter?.countercompanylnk_id || selectedCounterData?.counter_company_location_id || OrderPoDetails?.counter_company_location_id,
            location_code: selectedCounter?.location_code || selectedCounterData?.counter_location?.location_code || OrderPoDetails?.location_code || OrderPoDetails?.counter_location?.location_code,
            location_name: selectedCounter?.location_name || OrderPoDetails?.location_name || OrderPoDetails?.counter_location?.location_name,
            counter_name: selectedCounter?.counter_name || OrderPoDetails?.counter_name || OrderPoDetails?.counter_location?.counter_hdr_id?.counter_name,
            company_stockist_code: OrderPoDetails?.company_stockist_code || selectedStockist?.company_stockist_code,
            stockist_po_attachment_id: OrderPoDetails?.stockist_po_attachment_id,
            edit_type: "kam_edit",
            kam_type: OrderPoDetails?.kam_type,
            module_code: 4,
            poid_date: effectivePoDate,
            po_valid_date: poExpiryDate || getDefaultPoValidDate(effectivePoDate),
            poid_ref_number: effectivePoRefNo,
            // Fixed identifiers for the underlying stockist PO record — not overwritten by editing PO Ref No
            // stockist_po_number can be null on the fetched row (e.g. PO saved without a PO number) — the backend
            // requires a string, so fall back the same way ADD does (poid_number), then to "".
            reference_number: OrderPoDetails?.stockist_po_number || existingCounterPoData?.stockist_po_number || OrderPoDetails?.reference_number || OrderPoDetails?.poid_number || effectivePoRefNo || "",
            stockist_po_number: OrderPoDetails?.stockist_po_number || existingCounterPoData?.stockist_po_number || OrderPoDetails?.reference_number || OrderPoDetails?.poid_number || effectivePoRefNo || "",
            po_order_source: OrderPoDetails?.po_order_source,
            upload_source_type: uploadSourceType?.label,
            counter_po_status: counterPoStatus?.label,
            is_po_date_missing: !isPoDatePresent,
            is_po_number_missing: !isPoNumberPresent,
            is_po_netamount_missing: isNetAmountMissing,
            net_amount: calculatedNetAmountForSubmit(),
            cash_discount: Number(cashDiscountInput) || 0,
            cash_discount_perc: Number(cashDiscountPercentInput) || 0,
            // Audit/actor fields, kept consistent with the ADD payload above
            created_by: idTokenDetails?.sub || "",
            created_date: istISOString,
            source: "Web",
            latitude,
            longitude,
            actor_name: idTokenDetails?.name || "",
            actor_code: currentPsr?.emp_position_hdr?.position_code || "",
            actor_type: idTokenDetails?.["custom:role"],
            user_ucode: idTokenDetails?.["custom:ucode"] || "",
            ucode: generateUniqueID(),
            skip_notifications: true,
            orderProducts: lineItems.map(buildCounterPoEditOrderProduct),
        },
        event_type: "COUNTER_PO_EDIT",
        primary_key: OrderPoDetails?.stockist_po_attachment_id,
    });

    // Builds the request body for the original flow — POST /event/order, event_type ORDER_ADD
    const buildLegacyOrderPayload = (effectivePoDate: string, effectivePoRefNo: string, istISOString: string, latitude: number, longitude: number) => {
        const clustersOrdergroupData = [
            {
                stockist_id: OrderPoDetails?.stockist_id || selectedStockist?.stockist_id,
                company_stockist_code: OrderPoDetails?.company_stockist_code || selectedStockist?.company_stockist_code,
                medvol_stockist_code: "-",
                cluster_code: productsData?.[0]?.companyProducts?.item_division?.[0]?.cluster_code?.cluster_code,
                cluster_ucode: productsData?.[0]?.companyProducts?.item_division?.[0]?.cluster_code?.Cluster_UniqueId,
                ordergroup_id: productsData?.[0]?.companyProducts?.item_division?.[0]?.ordergroup_id?.ordergroup_id,
                cmp_countertag_id: productsData?.[0]?.counter_ff_div_id?.cmp_countertag_id,
                cndn_remarks_code: productsData?.[0]?.companyProducts?.item_division?.[0]?.ordergroup_id?.order_cndn_remarks?.cndn_remarks_code,
                order_mode: 1,
                headquarter_code: "-",
                headquarter_name: "-",
                bill_amount: grandTotal,
                discount_amount: 0,
                // Base fields (per_strip/pts/mrp/unit_rate/disc_perc/net_rate/gst/company_division_code) come from the
                // same per-product pricing snapshot the digitize flow sends — those fields are now editable here too.
                orderProducts: lineItems.map((item, index) => {
                    const base = buildCounterPoOrderProduct(item, index);
                    return {
                        ...base,
                        counter_product: 0,
                        // The ordered quantity is the Tablet/Strip-converted one the line builder already
                        // computed — re-reading item.quantity here sent the raw typed tablet count instead.
                        quantity: base.stockist_po_quantity,
                        module_code: 4,
                        discount_type: item?.priceIdentifier?.priceldentifier_desc,
                        // These two are the screen's separate "PO Quantity" inputs, not the converted quantity.
                        stockist_po_quantity: Number(item?.poQuantity) || 0,
                        stockist_po_freequantity: Number(item?.poFreeQuantity) || 0,
                    };
                }),
            },
        ];

        return {
            data: {
                company_code: Number(selectedCounterData?.company_code?.code),
                counter_code: selectedCounterData?.counter_location?.counter_hdr_id?.counter_code,
                counter_id: selectedCounterData?.counter_location?.counter_hdr_id?.counter_id,
                counter_ucode: selectedCounterData?.counter_location?.counter_hdr_id?.uid,
                counter_company_location_id: selectedCounterData?.counter_company_location_id,
                created_by: idTokenDetails?.sub || "",
                created_date: istISOString,
                source: "Web",
                latitude,
                longitude,
                actor_name: idTokenDetails?.name || "",
                actor_code: currentPsr?.emp_position_hdr?.position_code || "",
                actor_type: idTokenDetails?.["custom:role"],
                user_ucode: idTokenDetails?.["custom:ucode"] || "",
                cash_discount: Number(cashDiscountInput) || 0,
                cash_discount_perc: Number(cashDiscountPercentInput) || 0,
                ucode: generateUniqueID(),
                order_freetxt_remarks: "KAM",
                remarkby_type: "MV",
                location_code: selectedCounterData?.counter_location?.location_code,
                stockist_copy_path: "",
                counter_copy_path: OrderPoDetails?.po_file_path || "",
                clusters_ordergroup: clustersOrdergroupData,
                skip_notifications: true,
                poid_ref_number: effectivePoRefNo,
                poid_date: effectivePoDate,
                po_valid_date: poExpiryDate || getDefaultPoValidDate(effectivePoDate),
                module_code: 4,
                upload_type: "INST",
                company_divisioncode: productsData?.[0]?.counter_ff_div_id?.company_division_code?.company_divisioncode,
                stockist_po_attachment_id: OrderPoDetails?.stockist_po_attachment_id,
                // Now collected on this screen too (see Upload Source Type / Counter PO Status / Net Amount fields above)
                upload_source_type: uploadSourceType?.label,
                counter_po_status: counterPoStatus?.label,
                is_po_date_missing: !isPoDatePresent,
                is_po_number_missing: !isPoNumberPresent,
                is_po_netamount_missing: isNetAmountMissing,
                net_amount: calculatedNetAmountForSubmit(),
            },
            event_type: "ORDER_ADD",
        };
    };

    // COUNTER-PO-HEADER-SUBMIT ==================================================
    // Header "Submit" — saves only the Header Details (PO Ref No/Date/Expiry, Upload Source Type,
    // Counter PO Status) via COUNTER_PO_EDIT with edit_type "header_edit", without touching the
    // product list or approving. The backend mirrors the header onto every order linked to this PO
    // (PO number/date/expiry, and the PO Date onto the order's Order Placed Date), so the change
    // shows up wherever the order is used. Net amount / cash discount are deliberately left out —
    // they're tied to the product list and stay with Approve Order.
    const [isHeaderSubmitting, setIsHeaderSubmitting] = useState(false);
    const canSubmitHeader = Boolean(OrderPoDetails?.stockist_po_attachment_id && canEditProcessedPo && !isAlreadyRejectedStatus);

    // Header field checks shared by the header Submit and Approve Order — returns the first error
    // message, or null when the header is valid.
    const getHeaderValidationError = (): string | null => {
        if (isPoNumberPresent && !poRefNo.trim()) return "PO Ref No is required";
        if (poRefNo.length > 100) return "PO Ref No cannot exceed 100 characters";
        if (isPoDatePresent && !poDate) return "PO Date is required";
        if (isPoDatePresent && poExpiryDate && poDate && poExpiryDate < poDate) return "Expiry date cannot be before PO date";
        if (!uploadSourceType?.label) return "Upload Source Type is required";
        if (!counterPoStatus?.label) return "Counter PO Status is required";
        return null;
    };

    // Seeds the header inputs from a saved counter PO — shared by the reopened-PO prefill and the
    // Pending-PO header prefill effects below.
    const prefillHeaderFields = (savedPo: any) => {
        setPoRefNo(savedPo?.poid_ref_number || "");
        setPoDate(toDateInputValue(savedPo?.poid_date));
        setPoExpiryDate(toDateInputValue(savedPo?.po_valid_date));
        setIsPoDatePresent(!savedPo?.is_po_date_missing);
        setIsPoNumberPresent(!savedPo?.is_po_number_missing);
        setUploadSourceType(UPLOAD_SOURCE_TYPE_OPTIONS.find(o => o.label === savedPo?.upload_source_type) || null);
        setCounterPoStatus(COUNTER_PO_STATUS_OPTIONS.find(o => o.label === savedPo?.counter_po_status) || null);
    };

    const handleHeaderSubmit = async () => {
        if (isHeaderSubmitting) return;

        const headerError = getHeaderValidationError();
        if (headerError) {
            toast({ description: headerError, variant: "destructive" });
            return;
        }

        // Unlike Approve (which defaults a missing PO Date to today), a header-only submit keeps the
        // saved PO Date when "PO Date Present" is off — otherwise a linked order's Order Placed Date
        // would silently jump to today.
        const savedPoDate = toDateInputValue(existingCounterPoData?.poid_date || OrderPoDetails?.poid_date);
        const effectivePoDate = isPoDatePresent ? poDate : (savedPoDate || new Date().toISOString().slice(0, 10));
        const effectivePoRefNo = isPoNumberPresent ? poRefNo : OrderPoDetails?.poid_number;

        // The header exactly as saved — sent to the backend and folded back into route state below
        const savedHeader = {
            poid_date: effectivePoDate,
            po_valid_date: poExpiryDate || getDefaultPoValidDate(effectivePoDate),
            poid_ref_number: effectivePoRefNo,
            upload_source_type: uploadSourceType?.label,
            counter_po_status: counterPoStatus?.label,
            is_po_date_missing: !isPoDatePresent,
            is_po_number_missing: !isPoNumberPresent,
        };

        setIsHeaderSubmitting(true);
        try {
            // M10-16180: don't enter the PO against a counter whose event stream is blocked
            if (!(await checkCounterEventStatus())) return;

            // Runs the same /counter_po/validate check as the "Validate PO" button (duplicate PO number
            // within the FY), against the PO Date being saved — only continues to the save if it passes.
            const validation = await runCounterPoValidation(effectivePoDate);
            if (!validation.valid) {
                toast({ description: validation.message || "This PO could not be validated.", variant: "destructive" });
                return;
            }

            const payload = {
                data: {
                    ...savedHeader,
                    stockist_po_attachment_id: OrderPoDetails?.stockist_po_attachment_id,
                    edit_type: "header_edit",
                    kam_type: OrderPoDetails?.kam_type,
                    module_code: 4,
                    // Fixed identifiers for the underlying stockist PO record — same as buildCounterPoEditPayload
                    reference_number: OrderPoDetails?.stockist_po_number || OrderPoDetails?.reference_number,
                    stockist_po_number: OrderPoDetails?.stockist_po_number,
                },
                event_type: "COUNTER_PO_EDIT",
                primary_key: OrderPoDetails?.stockist_po_attachment_id,
            };
            const companyCode = String(Number(selectedCounterData?.company_code?.code) || Number(OrderPoDetails?.company_code));
            const res = await postCounterPoEdit(payload, companyCode);
            if (res?.status !== 200 && res?.status !== 201) throw new Error(res?.data?.message);
            toast({ description: "PO header details updated successfully", variant: "default" });
            // Stay on the PO entry screen — the header inputs already hold the submitted values; also
            // fold them into this route's state (OrderPoDetails/existingCounterPoData) so everything
            // reading the saved PO (validation date, saved-PO-date fallback, a browser refresh) sees
            // them too. Not re-fetched from the server: COUNTER_PO_EDIT is applied asynchronously via
            // its event, so an immediate getCounterPoDetails could still return the old header.
            navigate(`${location.pathname}${location.search}`, {
                replace: true,
                state: {
                    ...(location.state || {}),
                    OrderPoDetails: { ...OrderPoDetails, ...savedHeader },
                    existingCounterPoData: existingCounterPoData ? { ...existingCounterPoData, ...savedHeader } : existingCounterPoData,
                },
            });
        } catch (error) {
            console.error("Error updating counter PO header:", error);
            toast({ description: error instanceof Error && error.message ? error.message : "Failed to update PO header details", variant: "destructive" });
        } finally {
            setIsHeaderSubmitting(false);
        }
    };
    // COUNTER-PO-HEADER-SUBMIT ==================================================

    const isRequestInProgress = useRef(false);
    const handleApproval = useDebounceLeading(async () => {
        if (isRequestInProgress.current) return;
        isRequestInProgress.current = true;
        // M10-15732: the Approve button's "Approving..." state is only reflected once a state update
        // (setErrors below) forces a re-render — that re-render has been observed to reset scroll
        // to the top of the page. Capture and restore the scroll position around it defensively.
        const scrollYBeforeApproval = window.scrollY;
        const restoreScrollPosition = () => {
            requestAnimationFrame(() => window.scrollTo({ top: scrollYBeforeApproval }));
        };
        try {
            // Validation
            const headerError = getHeaderValidationError();
            if (headerError) {
                toast({ description: headerError, variant: "destructive" });
                return;
            }

            if (isEditingCounter && !selectedCounter) {
                toast({ description: "Counter is required", variant: "destructive" });
                return;
            }

            // Net amount is required by default; toggling it as "missing" skips this check entirely
            if (!isNetAmountMissing && !netAmountInput.trim()) {
                toast({ description: "Net Amount is required", variant: "destructive" });
                return;
            } else if (!isNetAmountMissing) {
                // M10-15868: same ₹10 tolerance InvoiceEntryScreen enforces between its calculated
                // total and the typed net amount.
                const netAmountDifference = Math.abs(calculatedNetAmountDisplay() - Number(netAmountInput));
                if (netAmountDifference > 10) {
                    toast({ description: "The difference between calculated amount and net amount should not exceed ₹10", variant: "destructive" });
                    return;
                }
            }

            if (lineItems.length === 0) {
                toast({ description: "Please select any one product", variant: "destructive" });
                return;
            }

            if (!isStatusVerified || !isCustomerVerified) {
                toast({ description: "Please Verified the above details", variant: "destructive" });
                return;
            }

            // Approve is only allowed once the "Validate PO" button has itself returned "valid" —
            // applies to every flow (KAM digitize and legacy), not just isKamPoDigitize.
            if (isHeaderSubmitPending) {
                toast({ description: counterPoValidation.message || "Please submit the header (Header Submit) before approving.", variant: "destructive" });
                return;
            }

            restoreScrollPosition();

            // Don't push another event onto a blocked counter PO stream — withEventCheck toasts and throws
            try {
                await withEventCheck(() => { }, counterPoStreamId, counterPoCompanyCode);
            } catch {
                return;
            }
            // M10-16180: same counter check as Header Submit
            if (!(await checkCounterEventStatus())) return;

            const now = new Date();
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istTime = new Date(now.getTime() + istOffset);
            const istISOString = istTime.toISOString().slice(0, -1);
            const todayDateStr = istISOString.split("T")[0];

            const effectivePoDate = isPoDatePresent ? poDate : todayDateStr;
            const effectivePoRefNo = isPoNumberPresent ? poRefNo : OrderPoDetails?.poid_number;

            let latitude = DEFAULT_COORDINATES.latitude;
            let longitude = DEFAULT_COORDINATES.longitude;

            // Fetch dynamic latitude and longitude
            try {
                const location = await getCurrentLocation();
                latitude = location.latitude;
                longitude = location.longitude;
            } catch (error) {
                console.log("Failed to get location, using default coordinates:", error);
                // toast({ description: "Unable to fetch location. Using default coordinates.", variant: "destructive" });
            }

            // The digitize flow and the legacy flow post to two different endpoints with two different payload shapes.
            // Within the digitize flow, reopening an already-saved PO (existingCounterPoData present) resubmits
            // via COUNTER_PO_EDIT instead of creating a new one via COUNTER_PO_ADD.
            //
            // Once a PO has become an order (order_codes present), editing it MUST go through
            // COUNTER_PO_EDIT regardless of kam_type — that's the only path that updates the
            // existing order (header fields, quantity/pricing, order totals — see
            // counterPo.service.ts's editCounterPo) instead of creating a second one. The legacy
            // ORDER_ADD path below is for first-time creation only: it doesn't know how to edit,
            // and blindly resubmitting through it would silently duplicate the order.
            const linkedOrderCodes = (OrderPoDetails?.order_codes ?? "").toString().split(",").map((c: string) => c.trim()).filter(Boolean);
            const alreadyHasOrder = linkedOrderCodes.length > 0;

            if (isKamPoDigitize || alreadyHasOrder) {
                try {
                    const isEdit = !!existingCounterPoData || alreadyHasOrder;
                    const payload = isEdit
                        ? buildCounterPoEditPayload(effectivePoDate, effectivePoRefNo, istISOString, latitude, longitude)
                        : buildCounterPoPayload(effectivePoDate, effectivePoRefNo, istISOString, latitude, longitude);
                    const res = isEdit
                        ? await postCounterPoEdit(payload, String(payload?.data?.company_code))
                        : await postCounterPo(payload, String(payload?.data?.company_code));
                    // Unlike /event/order (201 Created), these endpoints respond 200 OK on success
                    if (res?.status !== 200 && res?.status !== 201) throw new Error(res?.data?.message);
                    navigate("/transactions/orders/2");
                } catch (error) {
                    console.error("Error placing counter PO:", error);
                    toast({ description: error instanceof Error && error.message ? error.message : "Failed to place counter PO", variant: "destructive" });
                }
                return;
            }

            const payload = buildLegacyOrderPayload(effectivePoDate, effectivePoRefNo, istISOString, latitude, longitude);
            try {
                const res = await postPlaceOrder(payload, selectedCounterData?.company_code?.code);
                if (res?.status !== 201) throw new Error(res?.data?.message);
                navigate("/transactions/po/confirmation", { state: { orderDetails: res?.data?.data, stockistName: OrderPoDetails?.stockist_name || selectedStockist?.owner_name } });
            } catch (error) {
                console.error("Error placing order:", error);
            }
        }
        catch (error) {
            console.error("Error placing order:", error);
        } finally {
            isRequestInProgress.current = false;
            restoreScrollPosition();
        }
    }, 1000);

    // Table columns
    const columns = useMemo(() => [
        {
            header: "Product Name",
            accessorKey: "productName",
            cell: (info: any) => info.row.original.productName?.companyProducts?.name || "-",
        },
        {
            header: "Product Code",
            accessorKey: "productCode",
            cell: (info: any) => info.row.original.productCode?.companyProducts?.item_code || "-",
        },
        {
            header: "Price Identifier",
            accessorKey: "priceIdentifier",
            cell: (info: any) => info.row.original.priceIdentifier?.priceldentifier_desc || "-",
        },
        ...(isKamPoDigitize || isKam ? [{
            header: "PTR",
            accessorKey: "productCode",
            // Product List always shows the raw stored value, regardless of the Tablet/Strip toggle
            cell: (info: any) => {
                const row = info.row.original;
                return row.ptr || row.productCode?.companyProducts?.ptr || "0";
            },
        }] : []),
        // Per-product pricing snapshot (see PRICING_FIELDS) — shown for both flows
        ...PRICING_FIELDS.filter((field) => field.field !== "ptr").map((field) => ({
            header: field.label,
            accessorKey: field.field,
            cell: (info: any) => {
                if (field.field === "gst" && isWithoutGst) return "0"; //M10-15818
                const row = info.row.original;
                return row[field.field] || "0";
            },
        })),
        {
            header: "Quantity",
            accessorKey: "quantity",
            cell: (info: any) => {
                const row = info.row.original;
                return toDisplayValue("quantity", row.quantity, row.perStrip, row.priceUnit) || "0";
            },
        },
        {
            header: "Scheme Value",
            accessorKey: "schemeValue",
            cell: (info: any) => {
                const row = info.row.original;
                return toDisplayValue(schemeValueField(row.priceIdentifier?.id), row.schemeValue, row.perStrip, row.priceUnit) || "0";
            },
        },
        ...(showStockistPo ? [{
            header: "PO Quantity",
            accessorKey: "POQuantity",
            cell: (info: any) => info.row.original.poQuantity || "0",
        }] : []),
        ...(showStockistPo ? [{
            header: "PO Free Quantity",
            accessorKey: "POFreeQuantity",
            cell: (info: any) => info.row.original.poFreeQuantity || "0",
        }] : []),
        {
            header: "Discount Amount",
            accessorKey: "discountAmount",
            cell: (info: any) => `₹${info.row.original.discAmount}`,
        },
        {
            header: "Total",
            accessorKey: "total",
            cell: (info: any) => `₹${getLineTotal(info.row.original).toFixed(2)}`,
        },
        ...(canEditProcessedPo ? [{ //M10-15149
            header: "Actions",
            accessorKey: "actions",
            cell: (info: any) => {
                const index = info.row.index;
                return (
                    <button onClick={() => handleDeleteItem(index)}>
                        <img src={DeleteIcon} alt="delete" className="w-5 h-5" />
                    </button>
                );
            },
        }] : []),
        ...(canEditProcessedPo ? [{ //M10-15149
            header: "Edit",
            accessorKey: "edit",
            cell: (info: any) => {
                const index = info.row.index;
                return (
                    <button onClick={() => handleEditItem(index, info.row.original)}>
                        <img src={editPen} alt="edit" className="w-5 h-5" />
                    </button>
                );
            },
        }] : []),
    ], [OrderPoDetails, canEditProcessedPo, showStockistPo, isKam, isWithoutGst]);   //M10-15149 //M10-15818
    console.log(isRequestInProgress.current, "isRequestInProgress.current")

    // Fetches the Price Identifier list on demand — wired as the Price Identifier field's asyncListFunction so it's
    // called when the field is opened rather than eagerly at page load. company_divisioncode comes from the product
    // the user has picked by then (productsData is populated by searchProductsByField once they search a product),
    // falling back to the reopened-PO's saved line item for existingCounterPoData the same way the prefill effect does.
    const fetchPriceIdentifiers = async () => {
        const list = PRICE_IDENTIFIER_LIST;

        setPriceIdentifier(list);

        const fixedPriceOption = list.find((item: any) => item?.priceldentifier_desc === "Fixed Price");
        if (fixedPriceOption) {
            fixedPriceIdentifierRef.current = fixedPriceOption;
            setFormData((prev: any) => ({ ...prev, priceIdentifier: prev.priceIdentifier || fixedPriceOption }));
        }
        return list;
    };

    // Effects - FIXED: Only run once on mount
    useEffect(() => {
        // Unlinked counters have no counter_company_location_id yet until a counter is picked from the dropdown
        if (isCounterUnlinked) return;

        const fetchCounterData = async () => {
            const counterLocationId = window.sessionStorage.getItem("counterCompanyLocationId");
            const query = `?counter_company_location_id=${counterLocationId}`;
            try {
                const response = await getCounterByEmployee(query);
                dispatch(ordPoActions.setSelectedCounterData(response?.data?.data?.[0]));
            } catch (err) {
                console.log(err);
            }
        };

        setIsLoading(true);
        fetchCounterData().finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    // Fetch stockist details when productsData and selectedCounterData are available
    useEffect(() => {
        const fetchStockistDetails = async () => {
            if (!selectedCounterData?.counter_company_location_id || !productsData?.[0]) return;

            try {
                const res = await getStockist(
                    selectedCounterData?.counter_company_location_id,
                    productsData?.[0]?.companyProducts?.item_division?.[0]?.cluster_code?.cluster_code,
                    productsData?.[0]?.counter_ff_div_id?.company_division_code?.company_divisioncode,
                    4
                );
                if (res?.status !== 200) throw new Error(res?.data?.error);
                setStockistList(res?.data?.data);
            } catch (error) {
                console.log(error);
            }
        };

        fetchStockistDetails();
    }, [selectedCounterData?.counter_company_location_id, productsData]);

    useEffect(() => {
        if (isStockist) {
            setIsModelOpen(true);
        }
    }, [isStockist]);

    // Reopening a Processed PO doesn't guarantee the counter link is resolvable, and product/pricing data is no
    // longer eagerly loaded anywhere (Product Name/Code search it on demand — see searchProductsByField), so
    // priceIdentifier never loads from that path either. Fall back to fetching it from the saved line items' own
    // company_division_code — that's always present regardless of whether the counter link resolved, and unblocks
    // the prefill effect below.
    useEffect(() => {
        if (!existingCounterPoData || priceIdentifier?.length) return;

        setPriceIdentifier(PRICE_IDENTIFIER_LIST);
        const fixedPriceOption = PRICE_IDENTIFIER_LIST.find((item) => item?.priceldentifier_desc === "Fixed Price");
        if (fixedPriceOption && !fixedPriceIdentifierRef.current) fixedPriceIdentifierRef.current = fixedPriceOption;
    }, [existingCounterPoData, priceIdentifier?.length]);

    // Pre-fills the header fields and line items from a previously saved counter PO (Processed row reopened for
    // edit) once the price-identifier list is loaded, since mapping stockist_po_attachment_products.price_identifier
    // back to a {id, priceldentifier_desc} option requires that list. Runs only once via hasPrefilledExistingPo.
    // Dates (poid_date/po_valid_date) come back as full ISO datetimes — the <input type="date"> needs just the
    // date part, otherwise it renders blank.
    useEffect(() => {
        console.log(existingCounterPoData,"existingCounterPoData",existingCounterPoData?.poid_ref_number)
        if (!existingCounterPoData || hasPrefilledExistingPo.current || !priceIdentifier?.length) return;
        hasPrefilledExistingPo.current = true;

        prefillHeaderFields(existingCounterPoData);
        setIsNetAmountMissing(!!existingCounterPoData?.is_po_netamount_missing);
        setNetAmountInput(existingCounterPoData?.net_amount != null ? String(existingCounterPoData.net_amount) : "");
        setCashDiscountInput(existingCounterPoData?.cash_discount != null ? String(existingCounterPoData.cash_discount) : "");
        setCashDiscountPercentInput(existingCounterPoData?.cash_discount_perc != null ? String(existingCounterPoData.cash_discount_perc) : "");

        const prefilledLineItems = (existingCounterPoData?.stockist_po_attachment_products || []).map((op: any) => {
            const companyProducts = {
                item_code: op?.item_code,
                name: op?.product_name,
                ptr: op?.ptr,
                pts: op?.pts,
                mrp: op?.mrp,
                per_strip: op?.per_strip,
                gst_pc: op?.gst,
            };
            const productObj = { companyProducts };
            const ptr = Number(op?.ptr || 0);
            const discPerc = Number(op?.disc_perc || 0);

            return {
                productName: productObj,
                productCode: productObj,
                priceIdentifier: priceIdentifier.find((p: any) => p.id === op?.price_identifier) || null,
                schemeValue: op?.scheme_value ?? "",
                quantity: op?.stockist_po_quantity ?? "",
                poQuantity: "",
                poFreeQuantity: "",
                ptr: op?.ptr ?? "",
                perStrip: op?.per_strip ?? "",
                pts: op?.pts ?? "",
                mrp: op?.mrp ?? "",
                unitRate: op?.unit_rate ?? "",
                discAmount: Number(((ptr * discPerc) / 100).toFixed(2)) || 0,
                discPerc,
                gst: op?.gst ?? "",
                priceUnit: PRICE_UNIT_OPTIONS[0],
                // Identifies this as an existing saved item to update in place (vs stockist_po_attachment_products_id: 0
                // for a newly added item) in the COUNTER_PO_EDIT payload.
                stockistPoAttachmentProductId: op?.stockist_po_attachment_products_id || undefined,
            };
        });

        if (prefilledLineItems.length) setLineItems(prefilledLineItems);

        // Reopening a Processed PO shouldn't force a re-validate right away — treat it as already validated.
        // Only changing PO Ref No afterward should invalidate it again (see the reset effect below), so record
        // the ref no this prefill actually seeded as the "last validated" baseline to compare future edits against.
        setCounterPoValidation({ status: "valid" });
        validatedStockistPoNumberRef.current = existingCounterPoData?.poid_ref_number || OrderPoDetails?.poid_ref_number || OrderPoDetails?.poid_number || null;
    }, [existingCounterPoData, priceIdentifier]);

    // COUNTER-PO-HEADER-SUBMIT: a Pending PO opens without existingCounterPoData (PoOrderScreen only fetches
    // the saved counter PO for non-Pending rows), and a header-only Submit leaves it Pending — so without this,
    // header values saved via Submit would come back blank on reopen. Fetch the saved PO here and prefill ONLY
    // the header fields; products, Validate PO status and which API Approve uses are left exactly as before.
    // Only applied when a header was actually saved (Upload Source Type / Counter PO Status are mandatory on
    // every save), so a never-touched Pending PO still opens blank as it always has.
    useEffect(() => {
        const attachmentId = OrderPoDetails?.stockist_po_attachment_id;
        if (existingCounterPoData || !attachmentId) return;

        // Ignore a response that lands after the user has left the screen
        let isCancelled = false;
        getCounterPoDetails(attachmentId, OrderPoDetails?.company_code)
            .then((res: any) => {
                const savedPo = res?.data?.data?.data;
                if (isCancelled || (!savedPo?.upload_source_type && !savedPo?.counter_po_status)) return;
                prefillHeaderFields(savedPo);
            })
            .catch((error: any) => console.log(error));
        return () => {
            isCancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only on mount — must not overwrite what the user types afterwards

    // Once the KAM picks a counter from the instorder-counter-list dropdown, fetch its full details via the same
    // API/shape the rest of the screen already relies on (selectedCounterData), rather than the flat list-row fields
    useEffect(() => {
        if (!selectedCounter?.countercompanylnk_id) return;

        getCounterByEmployee(`?counter_company_location_id=${selectedCounter.countercompanylnk_id}`)
            .then((response: any) => dispatch(ordPoActions.setSelectedCounterData(response?.data?.data?.[0])))
            .catch((err: any) => console.log(err));
    }, [selectedCounter?.countercompanylnk_id]);

    // Fetch the counter list for this PO's stockist — kept ready whether or not a counter is already linked,
    // since the KAM can also open the edit dropdown on an already-linked counter to change it.
    // Not gated to kam-po-digitize/isStockist: the counter edit pen is available on any PO type now, so any
    // non-digitize flow (plain isKam included) needs this list too, straight from instorder-counter-list.
    const instorderCompanyCode = OrderPoDetails?.company_code || selectedCounterData?.company_code?.code;
    const instorderStockistUid = OrderPoDetails?.novu_metadata?.stockistData?.uid || selectedStockist?.uid;
    useEffect(() => {
        if (!instorderCompanyCode || !instorderStockistUid) return;

        getInstorderCounterList(instorderCompanyCode, instorderStockistUid)
            .then((res: any) => setCounterList(res?.data?.data || []))
            .catch((error: any) => console.log(error));
    }, [instorderCompanyCode, instorderStockistUid]);

    // A reopened Processed PO can have a counter_code but no resolvable counter_company_location_id (the counter_po
    // fetch doesn't return one) — submitting with that id null/undefined fails backend validation. Once the
    // instorder-counter-list loads, auto-match this PO's counter_code/location_code against it to recover a valid
    // countercompanylnk_id without making the KAM re-pick a counter they already have. If no match exists, fall
    // back to opening the Counter Name/Code editor so they can pick one explicitly instead of submitting broken data.
    useEffect(() => {
        if (!counterList?.length || selectedCounter || OrderPoDetails?.counter_company_location_id) return;

        const targetCounterCode = OrderPoDetails?.counter_code || OrderPoDetails?.counter_location?.counter_hdr_id?.counter_code;
        if (!targetCounterCode) return;

        const targetLocationCode = OrderPoDetails?.location_code || OrderPoDetails?.counter_location?.location_code;
        const matched = counterList.find((c: any) => c?.counter_code === targetCounterCode && (!targetLocationCode || String(c?.location_code) === String(targetLocationCode)))
            || counterList.find((c: any) => c?.counter_code === targetCounterCode);

        if (matched) {
            setSelectedCounter(matched);
        } else {
            setIsEditingCounter(true);
        }
    }, [counterList, selectedCounter, OrderPoDetails?.counter_code, OrderPoDetails?.counter_location?.counter_hdr_id?.counter_code, OrderPoDetails?.location_code, OrderPoDetails?.counter_location?.location_code, OrderPoDetails?.counter_company_location_id]);

    // Blocks the Add Product section until /api/v1/oms/counter_po/validate confirms this PO — the KAM must click
    // "Validate PO" and get a success response before they're allowed to start adding products.
    const stockistPoAttachmentId = OrderPoDetails?.stockist_po_attachment_id;
    const counterCodeForValidation = selectedCounter?.counter_code || selectedCounterData?.counter_location?.counter_hdr_id?.counter_code || OrderPoDetails?.counter_code || OrderPoDetails?.counter_location?.counter_hdr_id?.counter_code;
    const locationCodeForValidation = selectedCounter?.location_code || selectedCounterData?.counter_location?.location_code || OrderPoDetails?.location_code || OrderPoDetails?.counter_location?.location_code;

    // Reflects whatever the user has typed into the PO Ref No field, not just the saved value
    const stockistPoNumber = poRefNo || OrderPoDetails?.poid_ref_number || OrderPoDetails?.poid_number;
    // Falls back to today's date when this PO has no poid_date at all, rather than blocking validation on it.
    const poidDateForValidation = OrderPoDetails?.poid_date ? String(OrderPoDetails.poid_date).slice(0, 10) : new Date().toISOString().slice(0, 10);

    // A previous "valid" result was only for the old PO Ref No — once it changes, block Add Product again
    // until the KAM re-validates with the new value, so a stale validation can't cover a different PO number.
    const validatedStockistPoNumberRef = useRef<string | null>(null);
    useEffect(() => {
        if (validatedStockistPoNumberRef.current !== null && validatedStockistPoNumberRef.current !== stockistPoNumber) {
            setCounterPoValidation({ status: "idle" });
        }
    }, [stockistPoNumber]);

    // Shared by the "Validate PO" button and the header Submit (COUNTER-PO-HEADER-SUBMIT), which calls it
    // with the PO Date it's about to save. Resolves with the outcome so a caller can stop on failure; the
    // counterPoValidation state is updated either way, so the Add Product gate stays in sync.
    const runCounterPoValidation = async (poidDate: string = poidDateForValidation): Promise<{ valid: boolean; message?: string }> => {
        if (!stockistPoAttachmentId || !counterCodeForValidation || !locationCodeForValidation || !stockistPoNumber || !poidDate) {
            const message = "Missing PO details required to validate.";
            setCounterPoValidation({ status: "invalid", message });
            return { valid: false, message };
        }

        setCounterPoValidation({ status: "checking" });
        validatedStockistPoNumberRef.current = stockistPoNumber;

        try {
            const res: any = await validateCounterPo(
                {
                    stockist_po_attachment_id: stockistPoAttachmentId,
                    counter_code: counterCodeForValidation,
                    location_code: locationCodeForValidation,
                    stockist_po_number: stockistPoNumber,
                    poid_date: poidDate,
                },
                OrderPoDetails?.company_code
            );
            if (res?.status >= 200 && res?.status < 300) {
                setCounterPoValidation({ status: "valid" });
                return { valid: true };
            }
            const message = res?.data?.message || "This PO could not be validated.";
            setCounterPoValidation({ status: "invalid", message });
            return { valid: false, message };
        } catch (error: any) {
            const message = error?.response?.data?.message || "This PO could not be validated.";
            setCounterPoValidation({ status: "invalid", message });
            return { valid: false, message };
        }
    };


    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spinner />
            </div>
        );
    }

    return (
        <div>
            {/* Header Section */}
            {isCounterPoEventBlocked && (
                <div
                    className="py-0.5"
                    style={{
                        width: "100%",
                        backgroundColor: "rgba(117, 80, 250, 0.3)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <p
                        style={{
                            color: "#7450FA",
                            fontSize: "16px",
                            fontWeight: "bold",
                            textShadow: "1px 1px 2px #FFFFFF",
                            padding: "6px 0",
                        }}
                    >
                        We regret to inform you, The event has been "blocked" due to too many rigorous events in Queue.
                    </p>
                </div>
            )}
            {/* M10-16180: the counter itself has blocked events — the PO can't be entered against it */}
            {isCounterEventBlocked && !isCounterPoEventBlocked && (
                <div className="py-0.5 w-full flex justify-center items-center" style={{ backgroundColor: "rgba(117, 80, 250, 0.3)" }}>
                    <p className="py-1.5 text-base font-bold text-[#7450FA]" style={{ textShadow: "1px 1px 2px #FFFFFF" }}>
                        This counter has blocked events in queue — the PO cannot be entered until they are cleared.
                    </p>
                </div>
            )}
            <div className="p-5 px-0 border-b shadow-md">
                <Button
                    className="px-16 inline-flex mt-[1.16rem] gap-3 items-center text-[#7450FA] cursor-pointer text-base font-semibold"
                    onClick={() => navigate(OrderPoDetails?.po_file_path ? `/transactions/orders/2` : `/transactions/orders/details`)}
                >
                    <img src={PurpleLeftArrowIcon} alt="back" className="mr-4" />
                    {OrderPoDetails?.po_file_path ? "Back to PO" : "Back to Counter"}
                </Button>
                <div className="w-full px-16">
                    <h1 className="text-[2rem] line-height-normal font-bold text-[#323136] not-italic">
                        {OrderPoDetails?.po_file_path ? OrderPoDetails?.poid_number : OrderPoDetails?.counter_name || OrderPoDetails?.counter_location?.counter_hdr_id?.counter_name}
                    </h1>
                    <div className="flex items-center justify-between gap-2 mt-2">
                        <div className="flex items-center gap-2">
                            <p className="px-2 py-1 m-0 not-italic text-sm line-height-normal font-semibold rounded-[0.625rem] inline-flex justify-center items-center bg-[#EAF4FF] text-[#00527C]">
                                {selectedCounterData?.counter_location?.counter_hdr_id?.counter_code}
                            </p>
                            <p className="text-[#5E4200] px-2 py-1 m-0 not-italic text-sm line-height-normal font-semibold rounded-[0.625rem] inline-flex justify-center items-center bg-[#FFF1E3] text-[.9rem]">
                                {selectedCounterData?.drph_lnk_type?.dr_ph_lnk_code}
                            </p>
                        </div>
                    </div>
                    {(OrderPoDetails?.po_file_path || canRejectPo || canRevokePoRejection || canReEditPoRejection || canRevertPo) && (
                        <div className="flex justify-end gap-x-1.5 ml-auto">
                            {/* PO-REJECTION-RESTRUCTURE ============================================ */}
                            {canRejectPo && (
                                <Button
                                    onClick={() => setRejectPoOpen(true)}
                                    className="h-10 px-4 flex items-center gap-2 rounded-md border border-red-500 bg-white text-sm text-red-600 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300 transition-colors duration-150"
                                >
                                    Reject PO
                                </Button>
                            )}
                            {canRevokePoRejection && (
                                <Button
                                    onClick={() => setRejectionRevokeOpen(true)}
                                    className="h-10 px-4 flex items-center gap-2 rounded-md border border-indigo-600 bg-white text-sm text-indigo-600 shadow-sm hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors duration-150"
                                >
                                    Revoke Rejection
                                </Button>
                            )}
                            {canReEditPoRejection && (
                                <Button
                                    onClick={() => setRejectionReEditOpen(true)}
                                    className="h-10 px-4 flex items-center gap-2 rounded-md border border-red-500 bg-white text-sm text-red-600 shadow-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-300 transition-colors duration-150"
                                >
                                    Re-edit Rejection
                                </Button>
                            )}
                            {/* PO-REJECTION-RESTRUCTURE ============================================ */}
                            {/* COUNTER-PO-REVERT ==================================================== */}
                            {canRevertPo && (
                                <Button
                                    onClick={() => setRevertPoOpen(true)}
                                    className="h-10 px-4 flex items-center gap-2 rounded-md border border-indigo-600 bg-white text-sm text-indigo-600 shadow-sm hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors duration-150"
                                >
                                    Revert PO
                                </Button>
                            )}
                            {/* COUNTER-PO-REVERT ==================================================== */}
                            {OrderPoDetails?.po_file_path && (
                                <Button
                                    onClick={handleDownloadPO}
                                    className="h-10 px-4 flex items-center gap-2 rounded-md border border-indigo-600 bg-white text-sm text-indigo-600 shadow-sm hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-300 transition-colors duration-150"
                                >
                                    <img src={Download} className="w-6 h-6" alt="download icon" />
                                    Download PO
                                </Button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Header Details */}
            <div className="px-4 mt-6">
                <div className="p-6 bg-white rounded-lg shadow-sm">
                    <header className="py-3 border-b border-[#D0D0D0] pb-4">
                        <h3 className="text-xl font-semibold">Header Details</h3>
                    </header>
                    <div className="grid grid-cols-3 gap-x-8 gap-y-6 mt-4 pb-2">
                        {headerData.map((item, index) => {
                            if (isEditingCounter && (item.label === "Counter Name" || item.label === "Counter Code")) {
                                return (
                                    <div key={index} className="flex items-center gap-2">
                                        <p className="w-1/2 font-medium text-gray-600">{item.label}:</p>
                                        <div className="w-64 flex items-start gap-2">
                                            <div className="flex-1">
                                                <Autocomplete
                                                    disabled={!canEditProcessedPo}  //M10-15149
                                                    onSelectionChange={(value: any) => {
                                                        setSelectedCounter(value);
                                                        setErrors(prev => ({ ...prev, counter: "" }));
                                                        // Collapse back to normal display once a counter is picked — Counter Code becomes
                                                        // the clickable link (below) that redirects to that counter's master-details page
                                                        setIsEditingCounter(false);
                                                    }}
                                                    placeholder={`Select ${item.label}`}
                                                    inputClassName="w-full"
                                                    suggestionList={counterList}
                                                    displayKey={item.label === "Counter Name" ? "counter_name" : "counter_code"}
                                                    additionalDisplayKey="location_code"
                                                    tertiaryDisplayKey="hospital_name"
                                                    additionalDisplayKeySearch
                                                    defaultValue={selectedCounter}
                                                    uniqueKey="counter_location_id"
                                                />
                                                {item.label === "Counter Code" && errors.counter && (
                                                    <span className="text-red-500 text-xs mt-1 block">{errors.counter}</span>
                                                )}
                                            </div>
                                            {/* Only offer Cancel when a counter was already linked — nothing to revert to otherwise */}
                                            {!isCounterUnlinked && (
                                                <button
                                                    type="button"
                                                    className="mt-2.5"
                                                    onClick={() => {
                                                        setIsEditingCounter(false);
                                                        setSelectedCounter(null);
                                                        setErrors(prev => ({ ...prev, counter: "" }));
                                                    }}
                                                >
                                                    <img src={crossIcon} alt="cancel" className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            }

                            return (
                                <div key={index} className="flex items-center gap-2">
                                    <p className="w-1/2 font-medium text-gray-600">{item.label}:</p>
                                    {item.label === "Counter Code" && item.value ? (
                                        <p
                                            className="font-semibold text-[#7450FA] cursor-pointer hover:underline"
                                            onClick={() => {
                                                const counterId = selectedCounterData?.counter_location?.counter_hdr_id?.counter_id;
                                                window.open(`/masters/counters/details/?id=${counterId}`, "_blank");
                                            }}
                                        >
                                            {item.value}
                                        </p>
                                    ) : (
                                        <p className="font-semibold text-gray-800">{item.value || '-'}</p>
                                    )}
                                    {item.label === "Stockist Name" && (
                                        <ToggleButton isOn={isCustomerVerified} onClick={handleCustomerToggle} disabled={!canEditProcessedPo} />    //M10-15149
                                    )}
                                    {item.label === "Counter Name" && (
                                        <ToggleButton isOn={isStatusVerified} onClick={handleStatusToggle} disabled={!canEditProcessedPo} />    //M10-15149
                                    )}
                                    {/* Lets the KAM change an already-linked counter too, not just fill in a missing one — available for any PO type */}
                                    {canEditProcessedPo && (item.label === "Counter Name" || item.label === "Counter Code") && ( //M10-15149
                                        <button type="button" onClick={() => setIsEditingCounter(true)}>
                                            <img src={editPen} alt="edit" className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            );
                        })}

                    </div>
                    <div className="flex flex-wrap items-center gap-x-10 gap-y-6 mt-6">
                        {[
                            (
                                <div key="po-number-present" className="flex items-center gap-2">
                                    <label className="font-medium text-gray-600">PO Number Present:</label>
                                    <ToggleButton isOn={isPoNumberPresent} onClick={handlePoNumberPresentToggle} disabled={!canEditProcessedPo} />
                                    {isPoNumberPresent && (
                                        <>
                                            <label className="ml-4 font-medium text-gray-600" htmlFor="poRefNo">PO Ref No:</label>
                                            <div className="w-64">
                                                <input
                                                    type="text"
                                                    id="poRefNo"
                                                    disabled={!canEditProcessedPo}  //M10-15149
                                                    placeholder="Enter PO Ref No"
                                                    className={`w-full border rounded-md px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed ${errors.poRefNo ? "border-red-500" : "border-gray-300"}`}
                                                    value={poRefNo}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setPoRefNo(value);
                                                        setErrors(prev => ({
                                                            ...prev,
                                                            poRefNo: value.length > 100 ? "PO Ref No cannot exceed 100 characters" : "",
                                                        }));
                                                    }}
                                                />
                                                {errors.poRefNo && <span className="text-red-500 text-xs mt-1 block">{errors.poRefNo}</span>}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ),
                            (
                                <div key="po-date-present" className="flex items-center gap-2">
                                    <label className="font-medium text-gray-600">PO Date Present:</label>
                                    <ToggleButton isOn={isPoDatePresent} onClick={handlePoDatePresentToggle} disabled={!canEditProcessedPo} />
                                    {isPoDatePresent && (
                                        <>
                                            <label className="ml-4 font-medium text-gray-600" htmlFor="poDate">PO Date:</label>
                                            <div className="w-64">
                                                <Input
                                                    enableArrows={false}
                                                    showIcon={false}
                                                    disabled={!canEditProcessedPo}  //M10-15149
                                                    placeholder={"DD-MM-YYYY"}
                                                    type="date"
                                                    value={poDate}
                                                    className="w-full py-1 text-left border border-gray-300 rounded-lg"
                                                    onChange={(e) => {
                                                        setPoDate(e.target.value);
                                                        setErrors(prev => ({ ...prev, poDate: "" }));
                                                    }}
                                                />
                                                {errors.poDate && <span className="text-red-500 text-xs mt-1 block">{errors.poDate}</span>}
                                            </div>
                                        </>
                                    )}
                                </div>
                            ),
                            <div key="po-expiry-date" className="flex items-center gap-2">
                                <label className="font-medium text-gray-600" htmlFor="poExpiryDate">PO Expiry Date:</label>
                                <div className="w-64">
                                    <Input
                                        enableArrows={false}
                                        showIcon={false}
                                        disabled={!canEditProcessedPo}  //M10-15149
                                        placeholder={"DD-MM-YYYY"}
                                        type="date"
                                        value={poExpiryDate}
                                        min={poDate}
                                        className="w-full py-1 text-left border border-gray-300 rounded-lg"
                                        onChange={(e) => {
                                            setPoExpiryDate(e.target.value);
                                            setErrors(prev => ({ ...prev, poExpiryDate: "" }));
                                        }}
                                    />
                                    {errors.poExpiryDate && <span className="text-red-500 text-xs mt-1 block">{errors.poExpiryDate}</span>}
                                </div>
                            </div>,
                            (
                                <div key="upload-source-type" className="flex items-center gap-2">
                                    <label className="font-medium text-gray-600">Upload Source Type:</label>
                                    <div className="w-64">
                                        <Autocomplete
                                            disabled={!canEditProcessedPo}  //M10-15149
                                            onSelectionChange={(value: any) => {
                                                setUploadSourceType(value);
                                                setErrors(prev => ({ ...prev, uploadSourceType: "" }));
                                            }}
                                            placeholder="Select Upload Source Type"
                                            inputClassName="w-full"
                                            suggestionList={UPLOAD_SOURCE_TYPE_OPTIONS}
                                            displayKey="label"
                                            defaultValue={uploadSourceType}
                                            uniqueKey="label"
                                        />
                                        {errors.uploadSourceType && <span className="text-red-500 text-xs mt-1 block">{errors.uploadSourceType}</span>}
                                    </div>
                                </div>
                            ),
                            (
                                <div key="counter-po-status" className="flex items-center gap-2">
                                    <label className="font-medium text-gray-600">Counter PO Status:</label>
                                    <div className="w-64">
                                        <Autocomplete
                                            disabled={!canEditProcessedPo}  //M10-15149
                                            onSelectionChange={(value: any) => {
                                                setCounterPoStatus(value);
                                                setErrors(prev => ({ ...prev, counterPoStatus: "" }));
                                            }}
                                            placeholder="Select Counter PO Status"
                                            inputClassName="w-full"
                                            suggestionList={COUNTER_PO_STATUS_OPTIONS}
                                            displayKey="label"
                                            defaultValue={counterPoStatus}
                                            uniqueKey="label"
                                        />
                                        {errors.counterPoStatus && <span className="text-red-500 text-xs mt-1 block">{errors.counterPoStatus}</span>}
                                    </div>
                                </div>
                            ),
                            (
                                <div key="without-gst" className="flex items-center gap-2">
                                    <label className="font-medium text-gray-600">Without GST:</label>
                                    <ToggleButton isOn={isWithoutGst} onClick={handleWithoutGstToggle} disabled={!canEditProcessedPo} />
                                </div>
                            ),
                        ].filter(Boolean)}
                    </div>
                    {/* COUNTER-PO-HEADER-SUBMIT: saves header-only changes (validates the PO first), see handleHeaderSubmit */}
                    {canSubmitHeader && (
                        <div className="flex justify-end mt-6">
                            <button
                                type="button"
                                onClick={handleHeaderSubmit}
                                disabled={isHeaderSubmitting}
                                className="h-9 px-5 bg-[#8051FF] hover:bg-[#6f3fe0] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-normal rounded-md shadow-sm transition duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-1"
                            >
                                {isHeaderSubmitting ? "Submitting..." : "Header Submit"}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* PO-REJECTION-RESTRUCTURE: only for a PO currently in Reject status, not merely one that has a rejection_code stamped (e.g. still pending review) */}
            {isAlreadyRejectedStatus && <PoRejectionDetailsCard data={existingCounterPoData ?? OrderPoDetails} />}

            {/* PO-REJECTION-RESTRUCTURE: a rejected PO has nothing left to add/approve — hide
                Add Product, Product List and Approve Order entirely instead of just disabling them */}
            {!isAlreadyRejectedStatus && (
            <>
            {/* Add Product Section — restyled to match InvoiceEntryScreen's "Invoice Details" card
                (border-2 card, title + collapse chevron header, collapsible body). Fields/logic unchanged. */}
            <div ref={addProductSectionRef} className="p-0 border-2 rounded-lg m-5">
              <div className="m-5">
                <div className="flex items-center justify-between">
                    <p className="mb-2 text-lg font-semibold">Add Product</p>
                    <Button className="w-10 h-10 bg-[#F1F1F1] border border-1-black" onClick={() => setIsAddProductExpanded(prev => !prev)}>
                        <img
                            className={`transition-transform duration-300 ${isAddProductExpanded ? "rotate-180" : ""}`}
                            src={downArrow}
                            alt="down-arrow"
                        />
                    </Button>
                </div>
                {/* Known/Unknown product selector — same radio-group styling as InvoiceEntryScreen's
                    product-type selector ("Ordered Products" is skipped, PO has no such concept). */}
                <div className="flex items-center mb-5 space-x-6">
                    <div className="flex items-center">
                        <input
                            type="radio"
                            id="knownProduct"
                            name="productType"
                            value="known"
                            checked={!isUnknownProduct}
                            disabled={isAddProductBlocked}
                            onChange={() => handleUnknownProductToggle(false)}
                            className="mr-2"
                        />
                        <label htmlFor="knownProduct" className="text-sm text-gray-500">
                            Company Products
                        </label>
                    </div>
                    <div className="flex items-center">
                        <input
                            type="radio"
                            id="unknownProduct"
                            name="productType"
                            value="unknown"
                            checked={isUnknownProduct}
                            disabled={isAddProductBlocked}
                            onChange={() => handleUnknownProductToggle(true)}
                            className="mr-2"
                        />
                        <label htmlFor="unknownProduct" className="text-sm text-gray-500">
                            Unknown Product
                        </label>
                    </div>
                </div>
                <div
                    className={`transition-all duration-500 ease-in-out ${isAddProductExpanded ? "max-h-[3000px] opacity-100" : "max-h-0 opacity-0 overflow-hidden"}`}
                    style={{ transitionProperty: "max-height, opacity" }}
                >
                {/* COUNTER-PO-HEADER-SUBMIT: the separate "Validate PO" button is gone — Header Submit runs the
                    same /counter_po/validate check, and a successful one unlocks Add Product. */}
                {isCounterEventBlocked && (
                    <div className="mb-4">
                        <p className="text-sm font-medium text-red-600">This counter has blocked events — products cannot be added until they are cleared.</p>
                    </div>
                )}
                {!isCounterEventBlocked && isHeaderSubmitPending && (
                    <div className="mb-4 flex items-center gap-3">
                        {counterPoValidation.status === "checking" && (
                            <p className="text-sm text-gray-500">Validating PO…</p>
                        )}
                        {counterPoValidation.status === "invalid" && (
                            <p className="text-sm font-medium text-red-600">{counterPoValidation.message || "This PO could not be validated — products cannot be added."}</p>
                        )}
                        {counterPoValidation.status === "idle" && (
                            <p className="text-sm text-gray-500">Products can only be added after the header is submitted (Header Submit).</p>
                        )}
                    </div>
                )}
                <div className="flex items-start gap-x-4">
                    <div className="grid grid-cols-4 gap-4 flex-1">
                        {/* Product Name */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1.5">Product Name</label>
                            {isUnknownProduct ? (
                                <Input
                                    ref={productNameInputRef}
                                    type="text"
                                    disabled={isAddProductBlocked}
                                    value={formData.productName?.companyProducts?.name || ""}
                                    placeholder="Product Name"
                                    onChange={(e: any) => handleUnknownProductChange("name", e.target.value)}
                                    className="px-3 rounded-lg text-base text-[#1B1A1F]"
                                />
                            ) : (
                                <Autocomplete
                                    key={`product-name-${productPickerKey}`}
                                    inputRef={productNameInputRef}
                                    disabled={isAddProductBlocked}
                                    onSelectionChange={(value: any) => handleChange("productName", value)}
                                    placeholder="Product Name"
                                    inputClassName="w-fit"
                                    suggestionList={filteredProducts}
                                    asyncListFunction={searchProductsByName}
                                    minCharsToSearch={3}
                                    displayKey="companyProducts.name"
                                    defaultValue={formData.productName}
                                    uniqueKey="companyProducts"
                                    isOptionInactive={(item: any) => !!item?.is_inactive} //M10-16315
                                />
                            )}
                        </div>

                        {/* Product Code */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1.5">Product Code</label>
                            {isUnknownProduct ? (
                                <Input
                                    type="text"
                                    disabled={isAddProductBlocked}
                                    value={formData.productCode?.companyProducts?.item_code || ""}
                                    placeholder="Product Code"
                                    onChange={(e: any) => handleUnknownProductChange("code", e.target.value)}
                                    className="px-3 rounded-lg text-base text-[#1B1A1F]"
                                />
                            ) : (
                                <Autocomplete
                                    key={`product-code-${productPickerKey}`}
                                    disabled={isAddProductBlocked}
                                    onSelectionChange={(value: any) => handleChange("productCode", value)}
                                    placeholder="Product Code"
                                    inputClassName="w-fit"
                                    suggestionList={filteredProducts}
                                    asyncListFunction={searchProductsByCode}
                                    minCharsToSearch={3}
                                    displayKey="companyProducts.item_code"
                                    defaultValue={formData.productCode}
                                    uniqueKey="id"
                                    isOptionInactive={(item: any) => !!item?.is_inactive} //M10-16315
                                />
                            )}
                        </div>

                        {/* Price Identifier */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1.5">Price Identifier</label>
                            <Autocomplete
                                disabled={isAddProductBlocked}
                                onSelectionChange={(value: any) => handleChange("priceIdentifier", value)}
                                placeholder="Price Identifier"
                                inputClassName="w-fit"
                                suggestionList={priceIdentifier || []}
                                asyncListFunction={fetchPriceIdentifiers}
                                isAsyncQuery={false}
                                displayKey="priceldentifier_desc"
                                defaultValue={formData.priceIdentifier}
                                uniqueKey="id"
                            />
                            {/* M10-14422 — Show existing slab values for the selected product */}
                            {formData?.productCode?.counter_product_slab?.length > 0 && (
                                <div className="text-red-600 text-xs mt-1 space-y-0.5">
                                    {formData.productCode.counter_product_slab.map((slab: any, idx: number) => (
                                        <div key={slab?.counterProductSlab_Id ?? idx} className="flex flex-wrap gap-x-2">
                                            {slab?.discountonptr_disp != null && (
                                                <span>Discount On PTR = {slab.discountonptr_disp}%</span>
                                            )}
                                            {slab?.fixedprice_disp != null && (
                                                <span>Fixed Price = ₹{slab.fixedprice_disp}</span>
                                            )}
                                            {slab?.schemeqty_disp != null && (
                                                <span>Scheme Qty = {slab.schemeqty_disp} + {slab.quantity_disp}</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Quantity */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-1.5">Quantity</label>
                            <Input
                                ref={quantityInputRef}
                                type="input"
                                disabled={isAddProductBlocked}
                                value={formData.quantity}
                                placeholder="Quantity"
                                onChange={(e: any) => {
                                    // Digitize flow allows decimal quantities (e.g. 12.5); other flow stays digits-only
                                    const rawValue = isKamPoDigitize ? e.target.value.replace(/[^0-9.]/g, "") : e.target.value;
                                    handleChange("quantity", rawValue);
                                }}
                                className="px-3 rounded-lg text-base text-[#1B1A1F]"
                                isError={formData.quantity.toString().replace(".", "").length > 4}
                                errorMessage="Maximum 4 digits allowed"
                                min="0"
                                max="9999"
                            />
                        </div>
                        {/* Scheme Value */}
                        {(isKamPoDigitize || isKam || !showStockistPo ||
                            ((OrderPoDetails?.kam_price_visibility === "N" || isKAMSession === 'N') && formData?.priceIdentifier?.priceldentifier == 1)) && (   //M10-12866
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1.5">Scheme Value</label>
                                    <Input
                                        type="text"
                                        disabled={isAddProductBlocked}
                                        value={formData.schemeValue}
                                        placeholder="Scheme Value"
                                        onChange={(e: any) => {
                                            const rawValue = isKamPoDigitize ? e.target.value.replace(/[^0-9.]/g, "") : e.target.value;
                                            handleChange("schemeValue", rawValue);
                                        }}
                                        className="px-3 rounded-lg text-base text-[#1B1A1F]"
                                        //M10-14422 — Fixed Price allows decimals (e.g., 75.25); other identifiers keep 4-digit limit
                                        isError={
                                            isKamPoDigitize
                                                ? !/^\d{0,4}(\.\d{0,2})?$/.test(formData.schemeValue.toString())
                                                : formData?.priceIdentifier?.id === 3
                                                    ? formData.schemeValue.toString() !== "" &&
                                                      !/^\d{1,4}(\.\d{0,2})?$/.test(formData.schemeValue.toString())
                                                    : formData.schemeValue.toString().replace(".", "").length > 4
                                        }
                                        errorMessage={
                                            isKamPoDigitize
                                                ? "Maximum 4 digits and 2 decimal places allowed"
                                                : formData?.priceIdentifier?.id === 3
                                                    ? "Enter a valid price (e.g., 75.25)"
                                                    : "Maximum 4 digits allowed"
                                        }
                                        min="0"
                                        max="9999"
                                    />
                                </div>
                            )}
                        {/* Discount % */}
                        {(
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1.5">Discount Amount</label>
                                <Input
                                    type="text"
                                    disabled={isAddProductBlocked}
                                    value={formData.discAmount}
                                    placeholder="Discount Amount"
                                    onChange={(e: any) => {
                                        const rawValue = e.target.value.replace(/[^0-9.]/g, "");
                                        handleChange("discAmount", rawValue);
                                    }}
                                    className="px-3 rounded-lg text-base text-[#1B1A1F]"
                                    isError={!/^\d{0,6}(\.\d{0,2})?$/.test(formData.discAmount.toString()) || Number(formData.discAmount) > Number(formData.ptr || 0)}
                                    errorMessage="Discount Amount cannot exceed PTR"
                                    min="0"
                                />
                                {Number(formData.discAmount) > 0 && (
                                    <p className="mt-1 text-xs text-gray-500">Discount %: {formData.discPerc}%</p>
                                )}
                            </div>
                        )}
                        {/* Discount Percentage */}
                        {(
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1.5">Discount Percentage</label>
                                <Input
                                    type="text"
                                    disabled={isAddProductBlocked}
                                    value={formData.discPerc}
                                    placeholder="Discount Percentage"
                                    onChange={(e: any) => {
                                        const rawValue = e.target.value.replace(/[^0-9.]/g, "");
                                        handleChange("discPerc", rawValue);
                                    }}
                                    className="px-3 rounded-lg text-base text-[#1B1A1F]"
                                    isError={!/^\d{0,3}(\.\d{0,2})?$/.test(formData.discPerc.toString()) || Number(formData.discPerc) > 100}
                                    errorMessage="Discount Percentage cannot exceed 100"
                                    min="0"
                                    max="100"
                                />
                            </div>
                        )}
                        {showStockistPo &&
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1.5">PO Quantity</label>
                                <Input
                                    type="input"
                                    value={formData.poQuantity}
                                    placeholder="PO Quantity"
                                    onChange={(e: any) => handleChange("poQuantity", e.target.value)}
                                    className="px-3 rounded-lg text-base text-[#1B1A1F]"
                                    isError={formData.poQuantity.toString().length > 4}
                                    errorMessage="Maximum 4 digits allowed"
                                    min="0"
                                    max="9999"
                                />
                            </div>
                        }
                        {showStockistPo &&
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-1.5">PO Free Quantity</label>
                                <Input
                                    type="input"
                                    value={formData.poFreeQuantity}
                                    placeholder="PO Free Quantity"
                                    onChange={(e: any) => handleChange("poFreeQuantity", e.target.value)}
                                    className="px-3 rounded-lg text-base text-[#1B1A1F]"
                                    isError={formData.poFreeQuantity.toString().length > 4}
                                    errorMessage="Maximum 4 digits allowed"
                                    min="0"
                                    max="9999"
                                />
                            </div>
                        }


                    </div>
                </div>

                {/* Selected Product Pricing Details */}
                {formData.productCode && (
                    <div className="mt-4 grid grid-cols-6 gap-4 rounded-lg bg-gray-50 p-4">
                        {/* Tablet/Strip is chosen per line item, not globally — it's part of formData and gets carried into the line item on Add/Update */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">Tablet / Strip</label>
                            <Autocomplete
                                onSelectionChange={(value: any) => handleChange("priceUnit", value)}
                                placeholder="Select Unit"
                                inputClassName="w-full"
                                suggestionList={PRICE_UNIT_OPTIONS}
                                displayKey="label"
                                defaultValue={formData.priceUnit}
                                uniqueKey="label"
                            />
                        </div>
                        {PRICING_FIELDS.map((item) => (
                            <div key={item.field}>
                                <label className="block text-xs font-semibold text-gray-500 mb-1">
                                    {item.label}
                                    {PER_STRIP_DIVIDED_FIELDS.includes(item.field) && formData.priceUnit?.label === "Tablet" && " (per tablet)"}
                                </label>
                                {isUnknownProduct ? (
                                    <Input
                                        type="text"
                                        value={(formData as any)[item.field]}
                                        placeholder={item.label}
                                        onChange={(e: any) => handleChange(item.field, e.target.value.replace(/[^0-9.]/g, ""))}
                                        className="px-3 py-1 rounded-lg text-sm text-[#1B1A1F]"
                                    />
                                ) : (
                                    <p className="px-3 py-1 text-sm text-[#1B1A1F]">
                                        {toDisplayValue(item.field, (formData as any)[item.field], formData.perStrip, formData.priceUnit) || "-"}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Discount vs PTR — flagged in red, blocks Add/Update above 40% until a reason is given */}
                {formData.productCode && getDiscountPercent(formData) && (
                    <p className="mt-2 text-sm font-semibold text-red-600">
                        Discount: {getDiscountPercent(formData).toFixed(2)}%
                        {getDiscountPercent(formData) > 40 && " — exceeds 40%, confirmation will be required to add this item"}
                    </p>
                )}

                {/* Add / Update Item — placed after pricing details, bottom-right like Invoice's Add Product button */}
                <div className="flex justify-end pb-5 mt-5 gap-x-2">
                    {addOrUpdateItemButton}
                </div>
                </div>
              </div>
            </div>

            {/* Product List + Calculations — mirrors InvoiceEntryScreen's two-column
                "Items Added" (9/12) + "Calculations" (3/12) layout. Design only, same data/logic. */}
            <div className="flex flex-col mt-5 mx-5 gap-x-5 [@media(min-width:1580px)]:flex-row">
            <div className="flex flex-col w-full gap-4 p-8 border rounded-lg [@media(min-width:1580px)]:w-9/12">
                <SubTable
                    title="Product List"
                    subtitle="Review and manage all items added"
                    showheader={true}
                    columns={columns}
                    data={lineItems}
                    enableShowError={false}
                />
            </div>

            {/* Calculations — mirrors InvoiceEntryScreen's right-side "Calculations" card */}
            <div className="w-full p-8 mt-10 border rounded-lg [@media(min-width:1580px)]:w-3/12 [@media(min-width:1580px)]:mt-0">
                    <p className="mb-2 text-lg font-semibold">Calculations</p>
                    <div className="flex flex-col pb-2 gap-y-1">
                        {/* PO-CASH-DISCOUNT — % and Amount reduce the Calculated Net Amount shown below */}
                        <p className="flex justify-between items-center py-2 text-sm">
                            <span className="mr-2">Cash Discount %</span>
                            <div className="">
                                <Input
                                    enableArrows={false}
                                    showIcon={false}
                                    type="text"
                                    disabled={!canEditProcessedPo}
                                    value={cashDiscountPercentInput}
                                    placeholder="0"
                                    onChange={(e: any) => handleCashDiscountPercentChange(e.target.value)}
                                    className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                                />
                            </div>
                        </p>
                        <p className="flex justify-between items-center py-2 text-sm">
                            <span className="mr-2">Cash Discount Amount</span>
                            <div className="">
                                <Input
                                    enableArrows={false}
                                    showIcon={false}
                                    type="text"
                                    disabled={!canEditProcessedPo}
                                    value={cashDiscountInput}
                                    placeholder="0"
                                    onChange={(e: any) => handleCashDiscountAmountChange(e.target.value)}
                                    className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                                />
                            </div>
                        </p>
                        {/* PO-CASH-DISCOUNT */}
                        {!isKamPoDigitize && isKam && (
                            <p className="flex justify-between items-center py-2 text-sm">
                                Grand Total
                                <span className="font-semibold text-indigo-600">₹{grandTotal}</span>
                            </p>
                        )}
                        {/* Calculated Net Amount — line-items total less the cash discount; never affected by the typed Net Amount below */}
                        <p className="flex justify-between items-center py-2 text-sm">
                            Calculated Net Amount
                            <span className="font-semibold">₹{calculatedNetAmountDisplay().toFixed(2)}</span>
                        </p>
                        {/* M10-15868: Net Amount shown last, like Invoice's "Net Inv. Amt." coming after
                            "Total (as calculated)" — purely a typed/verified value, checked against
                            Calculated Net Amount (±₹10) on submit, but never feeds back into it. */}
                        <p className="flex justify-between items-center py-2 text-sm">
                            <span className="mr-2 flex items-center gap-2">
                                Net Amount
                                <span className="text-xs text-gray-500">Not available</span>
                                <ToggleButton isOn={isNetAmountMissing} onClick={handleNetAmountMissingToggle} disabled={!canEditProcessedPo} />
                            </span>
                            {!isNetAmountMissing && (
                                <div className="">
                                    <Input
                                        type="text"
                                        disabled={!canEditProcessedPo}  //M10-15149
                                        value={netAmountInput}
                                        placeholder={`e.g. ${calculatedNetAmount}`}
                                        onChange={(e: any) => {
                                            setNetAmountInput(e.target.value);
                                            setErrors(prev => ({ ...prev, netAmount: "" }));
                                        }}
                                        className="w-24 py-1 text-right border border-gray-300 rounded-lg"
                                        isError={!!errors.netAmount}
                                        errorMessage={errors.netAmount}
                                    />
                                </div>
                            )}
                        </p>
                        {/* M10-15868: an Approved PO has nothing left to approve — remove the
                            button entirely rather than showing it disabled.
                            Same for permission: users without canEditProcessedPo shouldn't see
                            the button at all, not just have it disabled. */}
                        {canEditProcessedPo && (
                            <Button
                                isLoading={isRequestInProgress.current}
                                loaderSize="small"
                                disabled={isRequestInProgress.current || !canEditProcessedPo}   //M10-15149
                                type="submit"
                                className="w-full h-9 mt-5 font-normal text-white bg-[#8051FF] shadow-sm"
                                onClick={handleApproval}>
                                {isRequestInProgress.current ? `Approving...` : "Approve Order"}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            </>
            )}

            {/* Stockist Selection Modal */}
            {isStockist && isModelOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
                    <div className="flex flex-col gap-y-2 w-[340px] max-h-[318px] bg-white p-4 rounded-xl shadow-2xl">
                        <div className="flex justify-between items-center">
                            <p className="text-normal py-1 font-semibold text-[#49484C]">Select Stockist</p>
                            <Button className="bg-transparent -mr-3" onClick={() => {
                                if (selectedStockist.length == 0) {
                                    toast({ description: "Please select stockist", variant: "destructive" });
                                    return
                                }
                                setIsModelOpen(false)
                            }}>
                                <img src={crossIcon} alt="cross" className="w-5 h-5 font-bold invert cursor-pointer" />
                            </Button>
                        </div>
                        <p className="text-sm mb-2 mt-2 text-[#666666]">Stockist Code</p>
                        <Autocomplete
                            placeholder="Select Stockist"
                            suggestionList={stockistList}
                            displayKey="owner_name"
                            uniqueKey="company_stockist_code"
                            additionalDisplayKey="city_name"
                            additionalDisplayKeySearch={true}
                            QuaternaryDisplayKey="medvol_stockist_code"
                            QuaternaryDisplayKeySearch={true}
                            onSelectionChange={(e: any) => setSelectedStockist(e)}
                        />
                        <Button className="text-white bg-[#8051FF] mt-2" onClick={() => {
                            if (selectedStockist.length == 0) {
                                toast({ description: "Please select stockist", variant: "destructive" });
                                return
                            }
                            setIsModelOpen(false)
                            handleStatusToggle()
                            handleCustomerToggle()
                        }}>
                            Save Changes
                        </Button>
                    </div>
                </div>
            )}

            {/* High Discount Confirmation Modal */}
            {/* M10-15732: uses the shared Modal (Headless UI Dialog) instead of a raw overlay div so
                Tab/Shift+Tab is trapped inside Cancel/Confirm & Add instead of escaping to the form behind it. */}
            <Modal isOpen={showDiscountConfirm} isHeader={false} onCloseModal={handleCancelHighDiscount} zIndex={9999}>
                <div className="flex flex-col gap-y-3 w-[380px] bg-white p-5 rounded-xl">
                    <p className="text-base font-semibold text-[#49484C]">High Discount Detected</p>
                    <p className="text-sm text-gray-600">
                        Discount is {getDiscountPercent(formData).toFixed(2)}%, which exceeds 40%. Please confirm to proceed.
                    </p>
                    <div className="flex justify-end gap-2 mt-2">
                        <Button
                            className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700"
                            onClick={handleCancelHighDiscount}
                        >
                            Cancel
                        </Button>
                        <Button
                            ref={confirmHighDiscountButtonRef}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm"
                            onClick={handleConfirmHighDiscount}
                        >
                            Confirm & Add
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* PO-REJECTION-RESTRUCTURE ================================================ */}
            <Modal isOpen={rejectPoOpen} isHeader={false} onCloseModal={() => setRejectPoOpen(false)} zIndex={9999}>
                <PoRejectModal
                    stockistPoAttachmentId={rejectPoStockistAttachmentId}
                    companyCode={Number(selectedCounterData?.company_code?.code) || Number(OrderPoDetails?.company_code)}
                    onCloseModal={() => setRejectPoOpen(false)}
                    onSuccess={() => navigate("/transactions/orders/2")}
                />
            </Modal>
            <Modal isOpen={rejectionRevokeOpen} isHeader={false} onCloseModal={() => setRejectionRevokeOpen(false)} zIndex={9999}>
                <PoRejectionRevokeModal
                    stockistPoAttachmentId={rejectPoStockistAttachmentId}
                    companyCode={Number(selectedCounterData?.company_code?.code) || Number(OrderPoDetails?.company_code)}
                    onCloseModal={() => setRejectionRevokeOpen(false)}
                    onSuccess={() => navigate("/transactions/orders/2")}
                />
            </Modal>
            <Modal isOpen={rejectionReEditOpen} isHeader={false} onCloseModal={() => setRejectionReEditOpen(false)} zIndex={9999}>
                <PoRejectModal
                    mode="re-edit"
                    stockistPoAttachmentId={rejectPoStockistAttachmentId}
                    companyCode={Number(selectedCounterData?.company_code?.code) || Number(OrderPoDetails?.company_code)}
                    existingRejectionCode={rejectPoExistingRejectionCode}
                    existingRemarks={rejectPoExistingRemarks}
                    onCloseModal={() => setRejectionReEditOpen(false)}
                    onSuccess={() => navigate("/transactions/orders/2")}
                />
            </Modal>
            {/* PO-REJECTION-RESTRUCTURE ================================================ */}

            {/* COUNTER-PO-REVERT ======================================================== */}
            <Modal isOpen={revertPoOpen} isHeader={false} onCloseModal={() => setRevertPoOpen(false)} zIndex={9999}>
                <PoRevertModal
                    stockistPoAttachmentId={rejectPoStockistAttachmentId}
                    companyCode={Number(selectedCounterData?.company_code?.code) || Number(OrderPoDetails?.company_code)}
                    onCloseModal={() => setRevertPoOpen(false)}
                    onSuccess={() => navigate("/transactions/orders/2")}
                />
            </Modal>
            {/* COUNTER-PO-REVERT ======================================================== */}
        </div>
    );
};

export default PoOrdersProductScreen;